/**
 * @fishstank/contracts — TypeScript types for v2 JSON contracts.
 *
 * Source of truth: FISHSTANK_V2_JSON_CONTRACTS.md + lib/config.ts (constants).
 * This package is the single source of truth for contract shapes consumed by
 * both apps (apps/public, apps/admin) and emitted by the Worker. If the
 * contract shape changes, update BOTH this file AND the contracts doc in the
 * same commit.
 *
 * Slice 2 Checkpoint 1 scope:
 *   - Provenance envelope
 *   - EntitiesArtifact (v2/staging/entities.json)
 *   - Display-threshold constant re-export
 *
 * Slice 2 Checkpoint 3 scope:
 *   - RecapArtifact (v2/staging/recap.json) — parent events + one level of
 *     subentries for a single arc, with optional entity filter.
 *
 * Slice 2 Checkpoint 4 scope:
 *   - LiveArtifact (v2/staging/live.json) — top-mentioned entities,
 *     recent TTS entries, and a latest-archive-events rail. Supports
 *     two modes: `active_arc` (default; scopes mentions+TTS to the
 *     single active arc) and `universe` (mentions+TTS span all arcs,
 *     including arc_id NULL TTS rows). The latest-archive-events rail
 *     is mode-agnostic — always cross-arc.
 *
 * Slice 2 Checkpoint 5 scope:
 *   - Three Wiki* artifacts (v2/staging/wiki/{entity,arc,challenge}.json):
 *     * WikiEntityArtifact — entity hero + optional wiki_pages prose +
 *       aliases + social links + arc participations + related challenges.
 *     * WikiArcArtifact — arc hero + summary_md + participations +
 *       challenges (ordered) + recap-event count badge.
 *     * WikiChallengeArtifact — challenge hero + summary_md + arc ref +
 *       optional promoted-from-recap-event ref + entities on that event.
 *   - All three are read-only and per-row (one R2 key per slug/id).
 *   - 404 surfacing: missing slug / merged entity / missing id → 404 JSON
 *     from the Worker → SSR re-throws as a real 404.
 *
 * Not in scope yet: TtsArtifact (standalone), ArcsArtifact, suggestions
 * surfaces, wiki editor mutations. Those land when the corresponding
 * slice ships.
 */

// -- Constants (mirror lib/config.ts) ------------------------------------

/**
 * Below this mention count, renderers MUST hide `sentiment.positive_pct`.
 * Keep in lockstep with `lib/config.ts` `SENTIMENT_DISPLAY_MIN_MENTIONS`.
 * See FISHSTANK_V2_OPEN_QUESTIONS_RESOLVED.md #4.
 */
export const SENTIMENT_DISPLAY_MIN_MENTIONS = 100 as const

// -- Provenance envelope -------------------------------------------------

/**
 * Every v2 artifact is wrapped in this envelope. The Worker writes one
 * `artifact_writes` row per artifact emitted whose columns line up 1:1 with
 * these fields (publisher, deployment_id, schema_version, source_db_revision,
 * artifact_key, written_at). `sha256_self` is optional in Slice 1 and remains
 * optional in Slice 2 Checkpoint 1.
 */
export interface Provenance {
  publisher: string
  deployment_id: string
  written_at: string // ISO-8601
  schema_version: string
  source_db_revision: string
  artifact_key: string
  sha256_self?: string | null
}

export interface Envelope<TBody> {
  provenance: Provenance
  body: TBody
}

// -- Entity sub-shapes ---------------------------------------------------

/**
 * Mirrors `entity_kind` enum (migration 0011 — current taxonomy).
 * Authoritative product categories: fish, freeloader, host,
 * production, other. ('other' is the catch-all bucket for entities
 * that don't cleanly fit the main four — replaced 'robot' in 0011.)
 */
export type EntityKind =
  | 'fish'
  | 'freeloader'
  | 'host'
  | 'production'
  | 'other'

/**
 * Mirrors `entity_status` enum.
 */
export type EntityStatus = 'active' | 'retired' | 'merged'

/**
 * Mirrors `participation_role` enum.
 */
export type ParticipationRole =
  | 'competitor'
  | 'host'
  | 'guest'
  | 'commentator'
  | 'staff'
  | 'other'

/**
 * Mirrors `participation_status` enum.
 */
export type ParticipationStatus =
  | 'active'
  | 'eliminated'
  | 'withdrawn'
  | 'winner'
  | 'other'

export interface EntityAlias {
  alias: string
  is_primary: boolean
}

export interface EntitySocialLink {
  platform: string
  handle: string
  url: string | null
}

/**
 * Public portrait shape attached to an Entity. Hydrated by
 * `lib/entities-loader.ts` from `media_assets` joined via
 * `entities.avatar_asset_id`. Null on Entity when no portrait is
 * attached.
 *
 * `storage_key` is the absolute URL the portrait is served at —
 * historically named for legacy reasons. Renderers should treat it
 * as an `<img src=...>`-suitable URL (no further prefix needed).
 *
 * Width / height are non-null because the upload route requires
 * them. Admin extracts dimensions client-side via the File API +
 * Image element and posts them with the multipart form.
 */
export interface EntityPortrait {
  storage_key: string
  mime_type: string
  width: number
  height: number
}

/**
 * Receipt body returned by the worker's portrait upload route on
 * success. Same shape regardless of whether the upload created a
 * fresh `media_assets` row or UPSERTed an existing one (same R2 URL
 * → same row).
 *
 * Slice 1 of the entity-portraits build. The admin client uses the
 * `media_asset_id` to follow up with a PATCH /v2/staging/entities/:id
 * carrying `avatar_asset_id: <id>`; that's the moment the entity is
 * actually flipped to point at the new asset.
 */
export interface EntityPortraitUploadReceipt {
  /** `media_assets.id` — stringified bigint. */
  media_asset_id: string
  /** Absolute URL the portrait is served at. */
  url: string
  mime_type: 'image/png' | 'image/jpeg' | 'image/webp'
  width: number
  height: number
}

export type EntityPortraitUploadArtifact =
  Envelope<EntityPortraitUploadReceipt>

/**
 * Per-entity active-arc participation. Null when the entity is not
 * participating in the currently-active arc.
 *
 * Added in Slice 2 Checkpoint 1 (see FISHSTANK_V2_SLICE2_CHECKPOINT_1_PLAN.md
 * "Contract-doc extension decision"). The contracts doc was updated to match.
 */
export interface ActiveArcParticipation {
  arc_slug: string
  arc_title: string
  role: ParticipationRole
  status: ParticipationStatus
  joined_at: string | null // ISO-8601
  left_at: string | null // ISO-8601
  /**
   * Per-arc display alias text (migration 0014). NULL means "no
   * override; use `entity.display_name`". When non-null, the public
   * Players card renders this string as the primary display and
   * `aka <entity.display_name>` as a smaller secondary line.
   * Resolved server-side via LEFT JOIN on `entity_aliases.id`; the
   * FK id is intentionally NOT exposed on the public read path.
   */
  display_alias_text: string | null
}

/**
 * Per-entity summary of one arc the entity has participated in.
 * Lighter than `ActiveArcParticipation` — no joined_at / left_at, since
 * the public Players page uses this only to bucket entities into arc
 * groups, not to render timestamps. Always emitted as a list on every
 * `Entity` (empty when the entity isn't in any arc).
 *
 * Added for the Players-page grouping refinement.
 */
export interface ArcParticipationSummary {
  arc_slug: string
  arc_title: string
  role: ParticipationRole
  status: ParticipationStatus
  /**
   * Per-arc category override. NULL means "use the entity's
   * `entity_kind`". Same enum domain as `EntityKind`. Surfaced on
   * the public `Entity` so the Players page can subgroup an entity
   * differently per arc (e.g. Burt as fish in S3, freeloader in S5).
   * Introduced in migration 0013.
   */
  kind_override: EntityKind | null
  /**
   * Per-arc display alias text (migration 0014). NULL means "use
   * `entity.display_name`". When non-null, the public Players card
   * renders this string as the primary display name and shows a
   * smaller secondary line `aka <entity.display_name>`. Resolved
   * server-side via LEFT JOIN on `entity_aliases.id`. Same as the
   * field on `ActiveArcParticipation` but emitted on every per-arc
   * summary so the public page can resolve display by the bucket
   * being rendered, not just the active arc.
   */
  display_alias_text: string | null
}

/**
 * Thin reference to the site-wide currently-active arc, surfaced once
 * at the body level of `EntitiesBody`. Null when no arc has
 * `status='active'`.
 */
export interface ActiveArcRef {
  slug: string
  title: string
}

/**
 * Sentiment summary per entity. Null when the sentiment source is not yet
 * wired into v2 (true in Slice 2 Checkpoint 1 — no sentiment table exists
 * yet). When populated:
 *   - mention_count is the raw mention count across in-scope sources.
 *   - positive_pct is 0..100 when mention_count >= SENTIMENT_DISPLAY_MIN_MENTIONS,
 *     and null below that threshold. `0.0` is a real value (widely disliked)
 *     and MUST NOT be conflated with `null` (below threshold).
 *
 * Added in Slice 2 Checkpoint 1.
 */
export interface Sentiment {
  mention_count: number
  positive_pct: number | null
}

export interface Entity {
  slug: string
  display_name: string
  entity_kind: EntityKind
  status: EntityStatus
  color: string | null
  portrait: EntityPortrait | null
  aliases: EntityAlias[]
  social_links: EntitySocialLink[]
  active_arc_participation: ActiveArcParticipation | null
  /**
   * Every arc the entity has participated in, ordered by the arc's
   * own `display_order` (NULLS LAST). Empty array when the entity is
   * not in any arc.
   */
  arc_participations: ArcParticipationSummary[]
  sentiment: Sentiment | null
}

export interface EntitiesBody {
  entities: Entity[]
  /**
   * The site-wide currently-active arc, or null when no arc has
   * `status='active'`. Surfaced once at the body level so consumers
   * don't need to scan per-entity `active_arc_participation`.
   */
  active_arc: ActiveArcRef | null
}

export type EntitiesArtifact = Envelope<EntitiesBody>

// -- Render-time rule helpers -------------------------------------------

/**
 * Returns true if `positive_pct` should be rendered for this entity.
 * Encodes the sentiment-threshold rule:
 *   - Sentiment entirely absent (null): no.
 *   - Mention count below threshold: no.
 *   - positive_pct explicitly null: no (defensive; server should have
 *     already nulled it, but render path must not conflate 0.0 with null).
 *   - Otherwise: yes.
 */
export function shouldDisplaySentiment(
  sentiment: Sentiment | null,
): sentiment is Sentiment & { positive_pct: number } {
  if (sentiment === null) return false
  if (sentiment.mention_count < SENTIMENT_DISPLAY_MIN_MENTIONS) return false
  if (sentiment.positive_pct === null) return false
  return true
}

// -- Arc reference (used by Recap and future Arcs artifact) --------------

/**
 * `arc_status` enum mirror.
 */
export type ArcStatus = 'scheduled' | 'active' | 'completed' | 'cancelled'

/**
 * Thin reference to an arc, safe to embed inside other artifacts.
 *
 * `is_fallback_for_active` is true iff the arc was selected by the
 * archive-fallback rule on the recap route (no arc with status='active'
 * exists, so the loader walked back to the most-recent completed arc).
 * Public renderers use this to choose between "live season" copy and
 * "latest archived season" copy.
 */
export interface ArcRef {
  slug: string
  title: string
  status: ArcStatus
  is_fallback_for_active: boolean
  /**
   * Arc's `start_date` (date-only ISO string, e.g. "2026-03-15"). Null
   * when the arc has no recorded start date. The public Recap timeline
   * uses this together with each event's `happened_at` to compute
   * "Day N of the season" on the client; without this field, day
   * numbering would require a backend round-trip per page.
   *
   * Added for the recap-timeline refinement. Non-breaking; existing
   * consumers ignore the field.
   */
  start_date: string | null
}

// -- Recap sub-shapes ----------------------------------------------------

/**
 * Recap event type reference (`recap_event_types.slug` + `label`).
 * Null at the event level when the underlying row's
 * `event_type_id IS NULL`.
 */
export interface RecapEventTypeRef {
  slug: string
  label: string
}

/**
 * Entity reference carried inside a recap event / subentry. Enough
 * information for the public card to render a chip and link to the
 * `/players/<slug>` detail page. `role` mirrors
 * `recap_event_entities.role` (free-text; nullable).
 */
export interface RecapEventEntityRef {
  slug: string
  display_name: string
  role: string | null
  /**
   * Hydrated from `entities.avatar_asset_id` → `media_assets.url`.
   * Null when the entity has no portrait attached. The public recap
   * card uses this to render a portrait stack on each event; absent
   * portraits fall through to a letter-chip on the render side.
   * Added for the recap-portraits refinement.
   */
  portrait_url: string | null
}

/**
 * A subentry is a child `recap_events` row whose `parent_event_id`
 * points at the surrounding parent. Depth is capped at 1 by the DB
 * trigger `enforce_recap_depth_le_1`, so subentries never carry
 * nested subentries.
 *
 * `id` is a stringified bigint for JSON precision safety (same
 * convention the entities loader uses for entity ids).
 */
export interface RecapSubentry {
  id: string
  title: string
  body_md: string | null
  happened_at: string | null
  display_order: number | null
  event_type: RecapEventTypeRef | null
  entities: RecapEventEntityRef[]
}

export interface RecapEvent {
  id: string
  scope: 'global' | 'arc'
  title: string
  body_md: string | null
  happened_at: string | null
  display_order: number | null
  event_type: RecapEventTypeRef | null
  entities: RecapEventEntityRef[]
  /** Always an array. Empty when the parent has no subentries. */
  subentries: RecapSubentry[]
}

export interface RecapFilter {
  entity_slug: string
}

export interface RecapBody {
  /**
   * The arc this envelope describes. `null` only for the pathological
   * "no arcs at all" DB state — the UI renders the empty state.
   */
  arc: ArcRef | null
  /**
   * Mirrors the query the loader executed. `null` when no filter was
   * applied. Echoed so the page can render "Filtering by X" without
   * re-parsing the URL and so subsequent callers see the same shape.
   */
  filter: RecapFilter | null
  events: RecapEvent[]
}

export type RecapArtifact = Envelope<RecapBody>

// -- Live sub-shapes -----------------------------------------------------

/**
 * Mode the Live envelope was rendered in.
 *
 *  - `active_arc` (default) — top mentions and recent TTS are scoped
 *    to the single arc with status='active'. The latest-archive-events
 *    rail is still cross-arc; that's by design (the rail's job is to
 *    surface things outside the current arc).
 *  - `universe` — mentions and TTS span every arc, including TTS rows
 *    whose arc_id is NULL (manual entries / off-arc transcripts).
 *
 * The body echoes the resolved mode so consumers don't have to re-parse
 * the URL.
 */
export type LiveMode = 'active_arc' | 'universe'

/**
 * Top-mentioned entity row. `mention_count` is the SUM of counts across
 * `tts_entry_entities` and `recap_event_entities` for in-scope rows
 * (subject to mode filtering and approved-only review state on the
 * recap side). It is a raw signal — a single entity mentioned by both
 * a TTS row and a recap event counts twice.
 *
 * `entity_id` is a stringified bigint for JSON precision safety, same
 * convention the entities loader uses.
 */
export interface LiveMention {
  entity_id: string
  entity_slug: string
  display_name: string
  mention_count: number
  /**
   * Per-source breakdown so consumers can render "5 mentions
   * (3 TTS · 2 recap)" without a second fetch.
   */
  sources: {
    tts: number
    recap: number
  }
}

/**
 * Thin arc reference attached to TTS / archive-event rows so the
 * renderer can show an arc badge without joining back to the arcs
 * artifact. Distinct from the top-of-body `ArcRef` (which carries
 * the active arc + its fallback bit) because rows attached to other
 * arcs don't have a meaningful `is_fallback_for_active`.
 */
export interface LiveArcRef {
  slug: string
  title: string
  status: ArcStatus
}

export interface LiveTtsEntry {
  id: string
  body: string
  voice: string | null
  spoken_at: string | null // ISO-8601, may be null when ingest didn't capture it
  tts_source: 'live_stream' | 'archive' | 'manual'
  /**
   * Arc this TTS entry belongs to. Null in `universe` mode for
   * arc_id-NULL rows (e.g. manual entries from before an arc was
   * known). Always populated in `active_arc` mode (filtered to the
   * active arc).
   */
  arc: LiveArcRef | null
  entities: RecapEventEntityRef[]
}

/**
 * Tile for the latest-archive-events rail. The rail surfaces the most
 * recently published recap parent events across every arc, so each
 * tile carries its own arc ref (lets the renderer link to
 * `/recap/<arc_slug>` and label the tile with the arc title).
 */
export interface LiveArchiveEvent {
  id: string
  title: string
  happened_at: string | null // ISO-8601
  published_at: string // ISO-8601, never null (review_state='approved' fills it)
  scope: 'global' | 'arc'
  /**
   * Arc this event was published under. Null only when the underlying
   * recap event has `scope='global'` and `arc_id IS NULL`. For
   * scope='arc' rows this is always populated.
   */
  arc: LiveArcRef | null
  event_type: RecapEventTypeRef | null
  entity_count: number
}

export interface LiveBody {
  /**
   * Mode the body was assembled in. Echoed so consumers can render
   * UI state from the body alone. Same `LiveMode` value the Worker
   * received via `?mode=`.
   */
  mode: LiveMode
  /**
   * Active arc as resolved by the same rule the recap loader uses.
   * `null` when no arcs exist at all (the pathological empty-DB
   * state). In `universe` mode this is still populated when an
   * active arc exists — useful for rendering the "currently
   * active: <X>" header even when the rest of the body spans all
   * arcs.
   */
  arc: ArcRef | null
  top_mentions: LiveMention[]
  recent_tts: LiveTtsEntry[]
  latest_archive_events: LiveArchiveEvent[]
}

export type LiveArtifact = Envelope<LiveBody>

// -- Wiki sub-shapes -----------------------------------------------------

/**
 * Mirrors the `wiki_page_status` enum from migration 0001. The DB also
 * carries `'review'` as a fourth state; we surface it on the Worker
 * contract so the admin table can render it correctly. Public renderers
 * generally only care about `'published'` vs not.
 */
export type WikiPageStatus = 'draft' | 'review' | 'published' | 'archived'

/**
 * Thin entity reference safe to embed inside any Wiki body for
 * cross-linking. Smaller than the full `Entity` type used by
 * `EntitiesArtifact` because Wiki cross-link sections only need
 * the slug + display name to render a chip + drill-in URL.
 */
export interface WikiEntityRef {
  slug: string
  display_name: string
}

/**
 * Thin arc reference for Wiki cross-links. Distinct from `ArcRef`
 * (used by Recap) because Wiki refs do NOT carry the
 * `is_fallback_for_active` bit — Wiki pages are addressed by explicit
 * slug, never by archive-fallback resolution.
 */
export interface WikiArcRef {
  slug: string
  title: string
  status: ArcStatus
}

/**
 * Thin challenge reference for Wiki cross-links. `id` is a stringified
 * bigint (same JSON-precision convention used everywhere). Challenges
 * have no slug column in v2, so addressing is by id; `arc_slug` is
 * carried so renderers can build `/wiki/challenge/<id>` plus an arc
 * label without a second fetch.
 */
export interface WikiChallengeRef {
  id: string
  arc_slug: string
  display_order: number
  title: string
}

// -- Wiki entity body ----------------------------------------------------

/**
 * Wiki body for a single entity. Hybrid shape:
 *   - `entity` is the structured entity record (always present when the
 *     route resolves; missing slug → 404 from the Worker).
 *   - `wiki_page` is the optional editable-prose layer (null when no
 *     `wiki_pages` row exists for the entity).
 *   - `participations` + `related_challenges` are the structured
 *     linked-data sections that prove the linked-data product truth.
 *
 * The renderer combines all four to produce a hybrid page; CP5 does
 * not render markdown — `overview_md` / `body_md` are surfaced as
 * preformatted text.
 */
export interface WikiEntityBody {
  entity: {
    slug: string
    display_name: string
    entity_kind: EntityKind
    status: EntityStatus
    short_bio: string | null
    aliases: EntityAlias[]
    social_links: EntitySocialLink[]
  }
  /**
   * `null` when the entity has no `wiki_pages` row yet. Renderers
   * should treat this as "developing — no wiki page started" without
   * confusing it with the `is_developing` flag (which can also be
   * true on a page that DOES exist but is sparse).
   */
  wiki_page: {
    slug: string
    title: string
    status: WikiPageStatus
    overview_md: string | null
    body_md: string | null
    /**
     * Server-computed mirror of `wiki_pages_is_developing(p)` from
     * migration 0004. True when the page is draft, has no overview,
     * or has a body shorter than DEVELOPING_BODY_MIN_CHARS.
     */
    is_developing: boolean
  } | null
  participations: Array<{
    arc: WikiArcRef
    role: ParticipationRole
    status: ParticipationStatus
  }>
  /**
   * Challenges whose promoted recap event includes this entity. Empty
   * array when the entity has no recap-event participations attached
   * to a promoted-to-challenge event. Acts as the linked-data drill-in
   * from entity → challenge.
   */
  related_challenges: WikiChallengeRef[]
}

export type WikiEntityArtifact = Envelope<WikiEntityBody>

// -- Wiki arc body -------------------------------------------------------

/**
 * Wiki body for a single arc. The arc itself is the source of truth
 * (no separate `wiki_pages` row for arcs in v2). `summary_md` is the
 * editable-prose column; `participations` + `challenges` are the
 * structured linked-data sections.
 *
 * `recap_event_count` is the count of approved parent-level recap
 * events scoped to the arc. Carried so the renderer can add a
 * badge ("12 events recorded") without a second fetch. Subentries
 * are NOT counted.
 */
export interface WikiArcBody {
  arc: {
    slug: string
    title: string
    status: ArcStatus
    arc_type: ArcType
    start_date: string | null
    end_date: string | null
    summary_md: string | null
    /**
     * Optional entity ref. Populated only when `arcs.winner_entity_id`
     * is set AND the referenced entity is not soft-merged. Same
     * soft-merge filtering convention the entities loader uses.
     */
    winner: WikiEntityRef | null
  }
  participations: Array<{
    entity: WikiEntityRef
    role: ParticipationRole
    status: ParticipationStatus
  }>
  /** Ordered by `challenges.display_order ASC`. */
  challenges: WikiChallengeRef[]
  /** Approved-only count, parents only (subentries excluded). */
  recap_event_count: number
}

export type WikiArcArtifact = Envelope<WikiArcBody>

// -- Wiki challenge body -------------------------------------------------

/**
 * Wiki body for a single challenge. The challenge itself is the source
 * of truth. `summary_md` is the editable-prose column; `arc` +
 * `promoted_from_recap_event` + `entities` are the structured
 * linked-data sections.
 *
 * `promoted_from_recap_event` is null when the challenge was created
 * directly (not promoted from a recap event). When present, the
 * `entities` array carries the recap-event-attached entities so the
 * renderer can show "Featured: alice, bob, …".
 */
export interface WikiChallengeBody {
  challenge: {
    id: string
    title: string
    display_order: number
    summary_md: string | null
    occurred_at: string | null
  }
  arc: WikiArcRef
  promoted_from_recap_event: {
    id: string
    title: string
    happened_at: string | null
  } | null
  /**
   * Entities attached to the promoted recap event. Empty array when
   * `promoted_from_recap_event` is null OR when no entities are
   * attached. Soft-merged entities filtered out.
   */
  entities: WikiEntityRef[]
}

export type WikiChallengeArtifact = Envelope<WikiChallengeBody>

// -- Wiki public index ---------------------------------------------------

/**
 * One row in the public wiki index. Surfaces the minimum fields needed
 * for the `/wiki` listing page to render link text + an "in development"
 * badge without a second fetch.
 */
export interface WikiPublicIndexRow {
  slug: string
  title: string
  /** null when the page has no entity FK or the entity is soft-merged. */
  entity_slug: string | null
  entity_display_name: string | null
  is_developing: boolean
  updated_at: string | null
}

export interface WikiPublicIndexBody {
  wiki_pages: WikiPublicIndexRow[]
  /**
   * True when the underlying query returned more than the CP5 cap
   * (`ADMIN_WIKI_LIST_CAP = 200`). The index UI uses this to advertise
   * "showing first 200" rather than implying the list is exhaustive.
   */
  truncated: boolean
}

export type WikiPublicIndexArtifact = Envelope<WikiPublicIndexBody>

// -- Wiki admin list -----------------------------------------------------

/**
 * One row in the admin-only wiki list. Every `wiki_pages` row in the
 * database surfaces here regardless of status — the admin operator
 * needs to see drafts/review/archived alongside published.
 *
 * `entity_slug` is null when the row has no entity FK OR the FK points
 * at a soft-merged entity. `is_developing` is the DB-authoritative
 * "still in development" flag (status != published OR body too short).
 */
export interface WikiAdminPageRow {
  id: string
  slug: string
  title: string
  status: WikiPageStatus
  is_developing: boolean
  entity_slug: string | null
  updated_at: string | null
}

export interface WikiAdminListBody {
  wiki_pages: WikiAdminPageRow[]
  /**
   * True when the DB has more rows than the cap (`ADMIN_WIKI_LIST_CAP`
   * in `lib/wiki-loader.ts`, currently 200). The admin UI uses this
   * flag to advertise "showing first N" rather than implying the
   * list is exhaustive.
   */
  truncated: boolean
}

export type WikiAdminListArtifact = Envelope<WikiAdminListBody>

// -- Wiki admin arc/challenge lists (CP10 Phase 3) ----------------------
//
// Two sibling admin-list surfaces fed by plain SELECTs against `arcs`
// and `challenges`. The admin SPA consumes them to render per-row Edit
// affordances on the Wiki page: an operator clicks "Edit" on an arc /
// challenge row and is routed to the narrow `summary_md` edit form.
//
// Shape discipline:
//   - Expose only columns the admin UI actually renders. This keeps the
//     admin cache small, audit diffs surgical, and the Wiki.tsx
//     component simple.
//   - `summary_md` is included because the table renders a short
//     preview of it; the edit form also loads the current value from
//     this payload without a second round trip.
//   - Both lists share the same `truncated` semantics as
//     `WikiAdminListBody`.

/**
 * One row on the admin-only arc list (`/v2/staging/wiki/admin/arcs.json`).
 * Shape surface area is intentionally narrow — only the columns the
 * admin UI renders or needs for the summary_md edit form.
 */
export interface WikiAdminArcRow {
  readonly id: string
  readonly slug: string
  readonly title: string
  readonly status: string
  readonly summary_md: string | null
  readonly updated_at: string | null
}

export interface WikiAdminArcsListBody {
  readonly arcs: readonly WikiAdminArcRow[]
  readonly truncated: boolean
}

export type WikiAdminArcsListArtifact = Envelope<WikiAdminArcsListBody>

/**
 * One row on the admin-only challenge list
 * (`/v2/staging/wiki/admin/challenges.json`). Carries the arc linkage
 * so the admin UI can group challenges under their arc without a
 * second fetch.
 */
export interface WikiAdminChallengeRow {
  readonly id: string
  readonly arc_id: string
  readonly arc_slug: string | null
  readonly arc_title: string | null
  readonly title: string
  readonly summary_md: string | null
  readonly updated_at: string | null
}

export interface WikiAdminChallengesListBody {
  readonly challenges: readonly WikiAdminChallengeRow[]
  readonly truncated: boolean
}

export type WikiAdminChallengesListArtifact = Envelope<WikiAdminChallengesListBody>

// -- TTS list -----------------------------------------------------------
//
// Slice 2 Checkpoint 6 scope:
//   - TtsListArtifact (v2/staging/tts/list.json) — paginated read of
//     `tts_entries` filtered to the public-safe subset
//     (`review_state='approved' AND hide_reason IS NULL`), with an
//     optional ILIKE search on the body, an entity-slug filter that
//     joins `tts_entry_entities`, an arc-slug filter, a half-open time
//     window on `spoken_at` (since inclusive, until exclusive), and
//     URL-driven pagination.
//   - Read-only and additive — no change to LiveArtifact, RecapArtifact,
//     EntitiesArtifact, or any of the WikiArtifacts. The Live page
//     continues to use its own `loadRecentTts` for the "recent TTS"
//     block; the new loader is the deeper browse/search surface that
//     lives at `/tts` under the More nav.
//   - Production messages are an explicitly parallel surface that the
//     CP6 contract does NOT touch — they share enough shape that they
//     can land in a follow-up checkpoint without a breaking change here.

/**
 * Server-derived hint about which axis is the dominant filter in the
 * current list view. Echoed on the body so renderers can highlight an
 * active filter chip without re-parsing the URL or duplicating the
 * derivation rule.
 *
 *   - `entity` — the request carried `entity_slug=...`. `arc_slug` is
 *      ignored for the purpose of this label (entity wins over arc).
 *   - `arc`    — the request carried `arc_slug=...` and no `entity_slug`.
 *   - `all`    — neither entity_slug nor arc_slug was supplied; the
 *      list spans every arc (including `arc_id IS NULL` rows).
 *
 * `q`, `since`, `until`, `page` do NOT participate in the mode label —
 * they refine the chosen scope but don't change which scope is in play.
 */
export type TtsListMode = 'all' | 'arc' | 'entity'

/**
 * Filter set echoed in the body. Renderers can re-populate the search
 * form from this without touching `Astro.url.searchParams` themselves.
 *
 * - `q`: trimmed and length-floored at 2 chars in the parser. If the
 *   user typed a single character, `q` here is `null` and the body is
 *   unfiltered (the search form still re-renders the typed value so
 *   the user sees what they entered — that lives in the renderer).
 * - `entity_slug` / `arc_slug`: lowercased + trimmed, never empty
 *   string. `null` means "no filter on this axis".
 * - `since`: ISO-8601 date or datetime, inclusive bound on `spoken_at`.
 * - `until`: ISO-8601 date or datetime, exclusive bound on `spoken_at`
 *   (half-open window — same convention every other v2 time filter
 *   uses).
 * - `page`: 1-indexed positive integer; values <= 0 are clamped to 1.
 */
export interface TtsListFilter {
  q: string | null
  entity_slug: string | null
  arc_slug: string | null
  since: string | null
  until: string | null
  page: number
}

/**
 * One row in the TTS list. Identical row shape to `LiveTtsEntry` so
 * the public renderer can reuse the same row component. Stringified
 * bigint ids — JSON precision safety.
 */
export interface TtsListItem {
  id: string
  body: string
  voice: string | null
  spoken_at: string | null // ISO-8601, may be null
  tts_source: 'live_stream' | 'archive' | 'manual'
  /**
   * Arc this row belongs to. Null when the underlying `tts_entries`
   * row has `arc_id IS NULL` (manual entries / off-arc transcripts).
   */
  arc: LiveArcRef | null
  entities: RecapEventEntityRef[]
}

/**
 * Pagination block — page-size capped, prev/next driven by `has_next`
 * and the page number; `truncated` fires when the total matching row
 * count would have exceeded `page_size * 50` (50-page cap). The
 * renderer uses `truncated` to advertise "result set truncated, narrow
 * filters" near the pager so the user understands why prev/next
 * eventually stop working.
 */
export interface TtsListPagination {
  page: number
  page_size: number
  total_returned: number
  has_next: boolean
  truncated: boolean
}

export interface TtsListBody {
  mode: TtsListMode
  filters: TtsListFilter
  items: TtsListItem[]
  pagination: TtsListPagination
}

export type TtsListArtifact = Envelope<TtsListBody>

// -- Production messages list -------------------------------------------
//
// Slice 2 Checkpoint 7 scope:
//   - ProductionMessagesListArtifact
//     (v2/staging/production_messages/list.json) — paginated read of
//     `production_messages` filtered to the public-safe subset
//     (`review_state='approved' AND hide_reason IS NULL`), with an
//     optional ILIKE search on the body, an arc-slug filter that joins
//     `arcs`, an exact-match `author` filter, and a half-open time
//     window on `occurred_at` (since inclusive, until exclusive).
//   - Read-only and additive — no change to LiveArtifact, RecapArtifact,
//     EntitiesArtifact, the WikiArtifacts, or TtsListArtifact. The CP7
//     contract is a separate envelope shape parallel to TTS; the two
//     surfaces share the row-shape vocabulary (arc badge, body, time)
//     but production messages are a distinct table with no link to
//     `entities`. This means there is no `entities` field on the row.
//   - The CP7 plan explicitly defers any unification with TTS. Each
//     surface ships its own contract; future slices may decide whether
//     to fold them, and that fold lives behind a new envelope.

/**
 * Server-derived hint about which axis is the dominant filter on the
 * current production-messages list view. Echoed on the body so renderers
 * can highlight an active filter chip without re-parsing the URL or
 * duplicating the derivation rule.
 *
 *   - `author` — the request carried `author=...`. `arc_slug` is ignored
 *      for the purpose of this label (author wins over arc, mirroring
 *      CP6's "entity wins over arc" rule).
 *   - `arc`    — the request carried `arc_slug=...` and no `author`.
 *   - `all`    — neither `author` nor `arc_slug` was supplied; the list
 *      spans every arc (including `arc_id IS NULL` rows).
 *
 * `q`, `since`, `until`, `page` do NOT participate in the mode label.
 */
export type ProductionMessagesListMode = 'all' | 'arc' | 'author'

/**
 * Filter set echoed in the body. Renderers can re-populate the search
 * form from this without touching `Astro.url.searchParams` themselves.
 *
 * - `q`: trimmed and length-floored at 2 chars in the parser. If the
 *   user typed a single character, `q` here is `null` and the body is
 *   unfiltered (the search form still re-renders the typed value).
 * - `arc_slug`: lowercased + trimmed, never empty string. `null` means
 *   "no filter on this axis".
 * - `author`: trimmed but case-preserved. Production-message authors
 *   are exact identifiers (handles, channel names, op tags) where
 *   case can carry meaning, so we do NOT lowercase. `null` means
 *   "no filter on this axis".
 * - `since`: ISO-8601 date or datetime, inclusive bound on `occurred_at`.
 * - `until`: ISO-8601 date or datetime, exclusive bound on
 *   `occurred_at` (half-open window — same convention every other v2
 *   time filter uses).
 * - `page`: 1-indexed positive integer; values <= 0 are clamped to 1.
 */
export interface ProductionMessagesListFilter {
  q: string | null
  arc_slug: string | null
  author: string | null
  since: string | null
  until: string | null
  page: number
}

/**
 * One row in the production-messages list. Stringified bigint id —
 * JSON precision safety. There is intentionally no `entities` field;
 * the schema has no `production_message_entities` link table and CP7
 * does not invent one.
 */
export interface ProductionMessagesListItem {
  id: string
  body: string
  author: string | null
  occurred_at: string | null // ISO-8601, may be null
  /**
   * Arc this message belongs to. Null when the underlying
   * `production_messages` row has `arc_id IS NULL`.
   */
  arc: LiveArcRef | null
}

/**
 * Pagination block — page-size capped, prev/next driven by `has_next`
 * and the page number; `truncated` fires when the total matching row
 * count would have exceeded `page_size * 50` (50-page cap). Identical
 * shape to `TtsListPagination` so renderers can share the pager
 * component if they want.
 */
export interface ProductionMessagesListPagination {
  page: number
  page_size: number
  total_returned: number
  has_next: boolean
  truncated: boolean
}

export interface ProductionMessagesListBody {
  mode: ProductionMessagesListMode
  filters: ProductionMessagesListFilter
  items: ProductionMessagesListItem[]
  pagination: ProductionMessagesListPagination
}

export type ProductionMessagesListArtifact =
  Envelope<ProductionMessagesListBody>

// -- Recap-event mutation (CP8) ------------------------------------------
//
// Slice 2 Checkpoint 8 scope:
//   - First write-side contract block. CP1-CP7 were all read-path; CP8
//     opens the admin mutation surface for `recap_events` (create + edit
//     with minimal parent/subentry handling). The posture this block
//     locks in — DB-first/R2-second, three-check CSRF, client-generated
//     idempotency keys, no PII in audit, typed ValidationError variants
//     remapped from DB trigger errors — is the carry-forward template
//     that every future admin-write slice (wiki editing, production-
//     messages moderation, TTS edits, etc.) will reuse.
//
//   - Additive-only: no existing type is renamed, widened, or narrowed.
//     The mutation routes are additive dispatch arms in the Worker; the
//     read envelopes (RecapArtifact, LiveArtifact, the Wiki*Artifacts,
//     TtsListArtifact, ProductionMessagesListArtifact) are untouched.
//     Slice 1 invariant #9.
//
//   - Two mutation routes (both defined here as request/response types,
//     wired by worker/src/recap-mutations.ts):
//       POST  /v2/staging/recap-events        → RecapEventMutationReceipt
//       PATCH /v2/staging/recap-events/:id    → RecapEventMutationReceipt
//
//   - One admin-only list route backing the extended /events page:
//       GET   /v2/staging/recap-events/admin/list.json
//         returns RecapEventsForAdminListArtifact (no
//         review_state='approved' filter — admin sees every state).
//
//   - One R2 artifact shape per mutation:
//       ${R2_PREFIX_V2_STAGING}mutations/recap_events/${id}/${idempotency_key}.json
//       body = RecapEventAuditArtifact. Written post-commit; on failure
//       the receipt's audit_mirror_deferred flag is set and the
//       `mutation_events.artifact_key` column stays NULL as a
//       drift-detector for a future sweep job.

/**
 * Admin-facing row shape for `recap_events`.
 *
 * Mirrors every column the edit-form can read or write — wider than the
 * public `RecapEvent` (which hides `review_state`, `published_at`,
 * `promoted_to_challenge_id`, `parent_event_id`, `created_at`,
 * `updated_at`) because the admin UI needs the full picture to render
 * the row, pre-fill the edit form, and decide whether the parent-picker
 * can still select this row (depth ≤ 1 rule).
 *
 * Stringified bigint ids — JSON precision safety (same convention as
 * every other v2 list shape).
 *
 * `arc` is the denormalized arc reference; null when `scope='global'`.
 * `event_type` is the denormalized event-type reference; null when the
 * row's `event_type_id IS NULL`.
 */
/**
 * Admin-side view of one row in `recap_event_entities`. Carries the
 * association id so the admin can DELETE by id, plus the joined
 * entity slug/display_name and (optional) portrait_url for chip
 * rendering. Free-text role is preserved per the schema.
 */
export interface RecapEventEntityAdminItem {
  /** `recap_event_entities.id` — stringified bigint. */
  id: string
  /** Joined entity slug. */
  slug: string
  /** Joined entity display name (entities.primary_name). */
  display_name: string
  /** `recap_event_entities.role` — nullable free-text. */
  role: string | null
  /** `media_assets.url` via entities.avatar_asset_id, or null. */
  portrait_url: string | null
}

export interface RecapEventForAdmin {
  id: string
  scope: 'global' | 'arc'
  arc_id: string | null
  /** Denormalized arc ref for display; null when scope='global'. */
  arc: LiveArcRef | null
  /**
   * Denormalized arc.start_date (date-only ISO, e.g. "2026-03-15") or
   * null when scope='global' or the arc has no start_date set. The
   * admin Events table uses this together with `happened_at` to
   * compute a "Day N of season" badge so operators can place each
   * event chronologically without doing arithmetic on raw timestamps.
   * Added per the admin-events usability slice.
   */
  arc_start_date: string | null
  parent_event_id: string | null
  event_type_id: string | null
  event_type: RecapEventTypeRef | null
  title: string
  body_md: string | null
  happened_at: string | null // ISO-8601
  display_order: number | null
  review_state: RecapReviewState
  published_at: string | null // ISO-8601
  promoted_to_challenge_id: string | null
  created_at: string // ISO-8601
  updated_at: string // ISO-8601
  /**
   * Entities currently attached to this event. Hydrated from
   * `recap_event_entities` joined to `entities` and (LEFT) to
   * `media_assets` for portrait_url. Sorted by display_name ASC.
   * Empty when the event has no attached entities.
   *
   * Added per the admin-events entity-attachment slice.
   */
  entities: RecapEventEntityAdminItem[]
}

/**
 * POST /v2/staging/recap-event-entities body. Whitelist-strict
 * parser; anything else in the body is silently ignored.
 */
export interface RecapEventEntityCreateRequest {
  /** Stringified bigint id of the recap event. Required. */
  recap_event_id: string
  /** Stringified bigint id of the entity to attach. Required. */
  entity_id: string
  /**
   * Free-text association role (subject / mentioned / tagged / …).
   * Trimmed; empty becomes null. Schema column allows null.
   */
  role?: string | null
  /** Required UUID v4. Client generates per form open. */
  idempotency_key: string
}

/**
 * DELETE /v2/staging/recap-event-entities/:id body.
 * `id` lives in the URL path; the body carries only the
 * idempotency_key.
 */
export interface RecapEventEntityDeleteRequest {
  idempotency_key: string
}

/**
 * Receipt body returned by POST. `row` is the full admin item
 * including the joined entity slug/display_name/portrait_url so
 * the SPA can render the new chip without re-fetching the list.
 */
export interface RecapEventEntityMutationReceiptBody {
  row: RecapEventEntityAdminItem
  /** Audit row id; stringified bigint. */
  mutation_event_id: string
  /** True only when the post-commit R2 mirror failed. */
  audit_mirror_deferred?: true
}

export type RecapEventEntityMutationReceipt =
  Envelope<RecapEventEntityMutationReceiptBody>

/**
 * Receipt body returned by DELETE. `prior_row` is the row that
 * was just removed (already-deleted replays return the audit's
 * snapshot verbatim).
 */
export interface RecapEventEntityDeleteReceiptBody {
  prior_row: RecapEventEntityAdminItem
  mutation_event_id: string
  audit_mirror_deferred?: true
}

export type RecapEventEntityDeleteReceipt =
  Envelope<RecapEventEntityDeleteReceiptBody>

/**
 * Typed validation-error variants for the recap-event-entity
 * mutation surfaces.
 */
export type RecapEventEntityValidationError =
  | { code: 'invalid_body' }
  | { code: 'recap_event_id_required'; field: 'recap_event_id' }
  | { code: 'invalid_recap_event_id'; field: 'recap_event_id' }
  | { code: 'unknown_recap_event'; field: 'recap_event_id' }
  | { code: 'entity_id_required'; field: 'entity_id' }
  | { code: 'invalid_entity_id'; field: 'entity_id' }
  | { code: 'unknown_entity'; field: 'entity_id' }
  | { code: 'entity_soft_merged'; field: 'entity_id' }
  | { code: 'role_too_long'; field: 'role'; max: number }
  | { code: 'already_attached'; field: 'entity_id' }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'idempotency_key_in_use'; field: 'idempotency_key' }
  | { code: 'attachment_not_found' }

/**
 * Server-enforced cap on `recap_event_entities.role`. Schema column
 * is unbounded TEXT; this guard protects the audit-row JSON from
 * abuse and keeps the chip rendering predictable. Tuned to match
 * the entity-alias source-field ceiling — same posture, same
 * "free-text label" use case.
 */
export const RECAP_EVENT_ENTITY_ROLE_MAX_LENGTH = 80

/**
 * R2 audit-artifact body for recap_event_entities mutations.
 * Same shape as EntityAliasAuditArtifactBody — DB-first / R2-second
 * mirror with prior_row + new_row both nullable to admit
 * create + delete actions. (Edit is not a surface here; recap
 * attachments are immutable on the server side — re-attach with
 * a different role to change it.)
 */
export interface RecapEventEntityAuditArtifactBody {
  target_table: 'recap_event_entities'
  target_id: string
  action: 'create' | 'delete'
  prior_row: RecapEventEntityAdminItem | null
  new_row: RecapEventEntityAdminItem | null
  mutation_event_id: string
  actor_cookie_hash: string
  idempotency_key: string
}

export type RecapEventEntityAuditArtifact =
  Envelope<RecapEventEntityAuditArtifactBody>

/**
 * `recap_review_state` enum values. Mirrors migration 0001_enums.sql.
 *
 * Doc-drift note (carried from the CP8 plan): the session handoff and
 * CP6/CP7 blockers docs refer to a 'pending' value, but the enum has
 * no such variant. CP8 does not touch `review_state` during
 * mutations; new rows land at the schema default ('unreviewed') and
 * edits leave the prior value untouched. Flagged here so future
 * write-side slices that DO flip `review_state` use the correct value.
 */
export type RecapReviewState =
  | 'unreviewed'
  | 'approved'
  | 'hidden'
  | 'flagged'

/**
 * POST /v2/staging/recap-events request body.
 *
 * `scope` must be 'arc' with a non-null `arc_id`, or 'global' with
 * `arc_id` null/omitted. The DB enforces this via the
 * `recap_events_scope_arc_match` CHECK constraint; the parser mirrors
 * it so clients get a typed error before the Worker touches Postgres.
 *
 * `parent_event_id`, if set, must point at a row in the same arc whose
 * own `parent_event_id` is NULL (depth ≤ 1). Two DB triggers
 * (`enforce_recap_depth_le_1`, `enforce_subentry_arc_match`) are the
 * source of truth; the admin UI's parent-picker filter is ergonomic
 * pre-filtering.
 *
 * `idempotency_key` is a client-generated UUIDv4 (admin SPA uses
 * `crypto.randomUUID()`). The Worker enforces
 * UNIQUE(mutation_events.idempotency_key); a replay with the same key
 * returns the prior receipt unchanged (double-click safety).
 *
 * Fields explicitly NOT acceptable in a CP8 create:
 *   - `review_state` (defaults to 'unreviewed'; a future approval
 *     slice owns the flip).
 *   - `published_at` (DB trigger fills it when review_state goes to
 *     'approved'; CP8 never writes that).
 *   - `promoted_to_challenge_id` (challenge-promotion is a separate
 *     editorial flow).
 */
export interface RecapEventCreateRequest {
  scope: 'global' | 'arc'
  arc_id?: string | null
  parent_event_id?: string | null
  event_type_id?: string | null
  title: string
  body_md?: string | null
  happened_at?: string | null // ISO-8601
  display_order?: number | null
  idempotency_key: string
}

/**
 * PATCH /v2/staging/recap-events/:id request body.
 *
 * Strict whitelist of editable fields: `title`, `body_md`,
 * `happened_at`, `event_type_id`, `display_order`, `parent_event_id`.
 * Fields omitted from the body are left untouched. `scope` and
 * `arc_id` are NOT editable in CP8 — changing the arc/scope of an
 * existing row has cascading implications for subentries and
 * public-surface indexing that belong to a future slice.
 *
 * `idempotency_key` required (same semantics as create). A replayed
 * PATCH with the same key returns the prior receipt; a PATCH with a
 * fresh key writes a new audit row (intentional).
 */
export interface RecapEventEditRequest {
  title?: string
  body_md?: string | null
  happened_at?: string | null // ISO-8601
  event_type_id?: string | null
  display_order?: number | null
  parent_event_id?: string | null
  idempotency_key: string
}

/**
 * Typed validation-error variants surfaced by the recap-event mutation
 * path. Every variant is a 400 from the Worker; the admin UI renders
 * the error inline under the offending field.
 *
 * `subentry_depth_exceeded`, `subentry_arc_mismatch`, and
 * `scope_arc_mismatch` are remaps from the three DB constraints on
 * `recap_events`:
 *   - `enforce_recap_depth_le_1`      → subentry_depth_exceeded
 *   - `enforce_subentry_arc_match`    → subentry_arc_mismatch
 *   - `recap_events_scope_arc_match`  → scope_arc_mismatch
 * The Worker catches the Postgres error by code + message pattern and
 * rethrows as the typed variant so the UI does not have to parse PG
 * error text. Unit tests assert each remap.
 *
 * The other variants (`title_required`, `title_too_long`,
 * `body_md_too_long`, `invalid_enum`, `invalid_uuid`, `invalid_iso`,
 * `invalid_integer`, `invalid_parent`, `unknown_event_type`,
 * `unknown_parent`, `not_found`, `no_editable_fields`) are surfaced by
 * the parser / loader before the transaction begins.
 */
export type RecapEventValidationError =
  | { code: 'title_required'; field: 'title' }
  | { code: 'title_too_long'; field: 'title'; max: number }
  | { code: 'body_md_too_long'; field: 'body_md'; max: number }
  | { code: 'invalid_enum'; field: string; allowed: string[] }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'invalid_iso'; field: string }
  | { code: 'invalid_integer'; field: string }
  | { code: 'invalid_parent'; field: 'parent_event_id' }
  | { code: 'scope_arc_mismatch'; field: 'arc_id' }
  | { code: 'subentry_depth_exceeded'; field: 'parent_event_id' }
  | { code: 'subentry_arc_mismatch'; field: 'parent_event_id' }
  | { code: 'unknown_event_type'; field: 'event_type_id' }
  | { code: 'unknown_parent'; field: 'parent_event_id' }
  | { code: 'no_editable_fields' }
  | { code: 'not_found' }

/**
 * Receipt returned on a successful mutation. Same provenance envelope
 * shape as the read path; the body carries the inserted/updated row,
 * the prior-row snapshot (null on create; full row on update), the
 * `mutation_events.id`, and an optional `audit_mirror_deferred` flag
 * that tells the admin UI the DB mutation landed but the R2 audit
 * write deferred (a future sweep job completes the mirror).
 */
export interface RecapEventMutationReceiptBody {
  row: RecapEventForAdmin
  prior_row: RecapEventForAdmin | null
  mutation_event_id: string
  /**
   * Only present (and only `true`) when the DB transaction committed
   * but the post-commit R2 audit-artifact write failed. The DB is the
   * authority; the admin UI should surface this as a soft warning, not
   * an error. A future sweep job detects rows whose
   * `mutation_events.artifact_key IS NULL` and retries the mirror.
   */
  audit_mirror_deferred?: boolean
}

export type RecapEventMutationReceipt =
  Envelope<RecapEventMutationReceiptBody>

/**
 * Error response shape for CP8 mutation routes.
 *
 * 400 responses carry `error: RecapEventValidationError` (one variant).
 * 403 responses carry `error: { code: 'csrf_failed'; reason: string }`.
 * 404 responses carry `error: { code: 'not_found' }`.
 * 500 responses carry `error: { code: 'internal'; message: string }`.
 *
 * The admin SPA narrows on `error.code` to render the right UI.
 */
export interface RecapEventMutationErrorResponse {
  ok: false
  error:
    | RecapEventValidationError
    | { code: 'csrf_failed'; reason: string }
    | { code: 'internal'; message: string }
}

/**
 * R2 audit artifact shape. One object per mutation; key convention:
 *   ${R2_PREFIX_V2_STAGING}mutations/recap_events/${id}/${idempotency_key}.json
 *
 * The body carries the prior+new row snapshots (so the artifact is
 * self-contained without a join back to `mutation_events`) plus the
 * `mutation_event_id` and the SHA-256 actor cookie hash. No PII.
 */
export interface RecapEventAuditArtifactBody {
  target_table: 'recap_events'
  target_id: string
  /**
   * Slice 3 (events delete) widens this additively from
   * `'create' | 'update'` to also include `'delete'`. Existing
   * create / update audit blobs validate unchanged under the wider
   * type. On delete, `prior_row` carries the full deleted snapshot
   * and `new_row` is null (mirrors the arc-participation B3 widening).
   */
  action: 'create' | 'update' | 'delete'
  prior_row: RecapEventForAdmin | null
  /**
   * Non-null on create + update; null only on delete. Migration
   * 0009 admits the NULL on the corresponding `mutation_events.new_row_json`
   * column.
   */
  new_row: RecapEventForAdmin | null
  mutation_event_id: string
  actor_cookie_hash: string
  idempotency_key: string
}

export type RecapEventAuditArtifact =
  Envelope<RecapEventAuditArtifactBody>

// -- Delete request + receipt + validation error (Slice 3) --------------
//
// Mirrors the Refinement Phase B3 ArcParticipationDelete* shapes —
// whitelist-strict body carrying just the idempotency key, a typed
// receipt body that hands back the deleted snapshot, and a closed
// validation-error union.
//
// FK posture (already enforced by migration 0003): deleting a
// recap_events row cascades through:
//   - `recap_events.parent_event_id` → ON DELETE CASCADE (subentries)
//   - `recap_event_entities.recap_event_id` → ON DELETE CASCADE
//   - `recap_event_sources.recap_event_id` → ON DELETE CASCADE
//   - `challenges.promoted_from_recap_event_id` → ON DELETE SET NULL
// No application-level child sweep is required.

/**
 * Whitelist-strict DELETE body. Just the `idempotency_key`. Cloudflare
 * Workers' `request.json()` reads bodies on DELETE requests; this
 * matches the body-borne idempotency posture of every other admin
 * mutation surface (CP8 create + edit, B3 arc-participation delete).
 */
export interface RecapEventDeleteRequest {
  idempotency_key: string
}

/**
 * Closed validation-error union for the delete surface. Distinct from
 * `RecapEventValidationError` so the SPA narrows on a tighter set:
 * delete has no field-level shape (no title, no body_md, no enum
 * fields). The trigger-remap variants from create/edit don't fire on
 * delete either — there are no triggers on DELETE for `recap_events`.
 */
export type RecapEventDeleteValidationError =
  | { code: 'invalid_body' }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'idempotency_key_in_use'; field: 'idempotency_key' }
  | { code: 'recap_event_not_found' }

/**
 * Receipt body returned on successful delete. Distinct from
 * `RecapEventMutationReceiptBody` so `deleted_row` is unambiguous
 * (no `row` / `prior_row` slot reuse).
 */
export interface RecapEventDeleteReceiptBody {
  /** Full snapshot of the deleted row. */
  deleted_row: RecapEventForAdmin
  /** stringified bigint — the `mutation_events` row id. */
  mutation_event_id: string
  /**
   * Only present (and only `true`) when the DB transaction committed
   * but the post-commit R2 audit-artifact write failed. Same posture
   * as create/edit.
   */
  audit_mirror_deferred?: true
}

export type RecapEventDeleteReceipt =
  Envelope<RecapEventDeleteReceiptBody>

/**
 * GET /v2/staging/recap-events/admin/list.json body.
 *
 * Admin-only list; no public filter (every review_state surfaces).
 * Ordering: `happened_at DESC NULLS LAST, id DESC` — mirrors the TTS
 * / production-messages admin convention.
 *
 * Pagination: same shape as the other v2 list surfaces
 * (`TtsListPagination`, `ProductionMessagesListPagination`) so the
 * admin renderer can reuse the pager component.
 */
export interface RecapEventsForAdminListPagination {
  page: number
  page_size: number
  total_returned: number
  has_next: boolean
  truncated: boolean
}

export interface RecapEventsForAdminListFilter {
  /** Optional arc-scope filter; null means "every arc and global". */
  arc_slug: string | null
  /** 1-indexed positive integer; <=0 is clamped to 1. */
  page: number
  /**
   * Optional day-of-season target. When set together with `arc_slug`,
   * the loader resolves it to the page that contains the first event
   * on that arc-day (DESC ordering, so it's the page where transitions
   * from "later days" to the target day occur). The resolved page is
   * echoed back via `pagination.page`; the filter itself is preserved
   * so the SPA can show "jumped to day N" UX without re-resolving.
   *
   * Ignored when `arc_slug` is null (day numbers are arc-relative).
   * Out-of-range values (≤ 0 or beyond the arc's known day count)
   * are clamped to the nearest valid day inside the loader.
   *
   * Added per the admin Events pagination slice.
   */
  jump_to_day?: number | null
}

/**
 * Admin-facing event-type ref. Distinct from the public
 * `RecapEventTypeRef` because the admin form needs the bigint id
 * (stringified) to populate the `event_type_id` field on a PATCH.
 */
export interface RecapEventTypeAdminItem {
  id: string
  slug: string
  label: string
}

export interface RecapEventsForAdminListBody {
  filters: RecapEventsForAdminListFilter
  items: RecapEventForAdmin[]
  pagination: RecapEventsForAdminListPagination
  /**
   * Full closed list of `recap_event_types` rows. Surfaced so the
   * admin Events form can populate its Type dropdown with every
   * option the DB allows — not just the subset already in use on
   * the current page of items. Sorted by `label` ASC for stable
   * UI ordering.
   */
  event_types: RecapEventTypeAdminItem[]
}

export type RecapEventsForAdminListArtifact =
  Envelope<RecapEventsForAdminListBody>

// -- Wiki mutation (CP10 Phase 1) ---------------------------------------
//
// Slice 2 Checkpoint 10 — Phase 1 scope:
//   - First admin mutation slice OUTSIDE of `recap_events`. The CP8
//     template (recap-event-write) proved DB-first/R2-second posture,
//     three-check CSRF, idempotency, audit rows, typed validation
//     errors. CP10 exercises every axis of that template against a
//     second target table (`wiki_pages`) to confirm the axis
//     (`mutation_events.target_table TEXT`, per-route CSRF action,
//     loader multiplication, contract family multiplication) is
//     actually extensible without re-opening the architecture.
//
//   - Two mutation routes (both defined here as request/response
//     types; wired by worker/src/wiki-mutations.ts):
//       POST  /v2/staging/wiki/entity-pages           → WikiMutationReceiptEnvelope
//       PATCH /v2/staging/wiki/entity-pages/:id       → WikiMutationReceiptEnvelope
//
//   - Phase 1 is entity-linked wiki pages only. Phase 2 adds arc
//     summary (`arcs.summary_md`) and challenge summary
//     (`challenges.summary_md`) edit routes that reuse these shapes.
//     The `WikiMutationTarget` union carries `'arcs'` and
//     `'challenges'` as forward-declared variants that Phase 1
//     receipts never emit.
//
//   - Additive-only: no rename, narrow, or removal of any existing
//     type or route. Slice 1 invariant #9.
//
//   - One R2 artifact shape per mutation:
//       ${R2_PREFIX_V2_STAGING}mutations/wiki_pages/${id}/${idempotency_key}.json
//     body = WikiAuditArtifactBody. Same DB-first/R2-second discipline
//     as CP8: `mutation_events.artifact_key` stays NULL on mirror
//     failure and the receipt carries `audit_mirror_deferred: true`.

/**
 * Audit target_table string for wiki mutations. Matches the loader's
 * TARGET_TABLE_* constants; wire-observable via
 * `WikiMutationReceiptBody.target_table`.
 *
 * Phase 1 emits `'wiki_pages'` only. `'arcs'` and `'challenges'` are
 * forward-declared for Phase 2; Phase 1 handlers never emit those
 * values, and the Phase 1 unit suite asserts this.
 */
export type WikiMutationTarget =
  | 'wiki_pages'
  | 'arcs'        // populated in Phase 2
  | 'challenges'  // populated in Phase 2

/**
 * POST /v2/staging/wiki/entity-pages request body.
 *
 * Every field is required — the CP10 create route carries no implicit
 * defaults beyond the `wiki_pages` schema defaults (which are all NULL
 * for `overview_md`, `body_md`, `is_developing_override`). The admin
 * SPA's create form fills each field or sends null explicitly for the
 * optional markdown columns.
 *
 * `idempotency_key` is a client-generated UUIDv4 (same contract as
 * CP8: `crypto.randomUUID()`). The Worker enforces
 * UNIQUE(mutation_events.idempotency_key); a replay with the same
 * key returns the prior receipt unchanged (double-click safety).
 *
 * `entity_id` is a stringified bigint (JSON precision safety). The
 * loader enforces the one-wiki-page-per-entity rule via
 * `SELECT ... FOR UPDATE` before the INSERT — see audit §W3.
 */
export interface WikiEntityPageCreateRequest {
  readonly idempotency_key: string   // uuid v4
  readonly title: string
  readonly slug: string
  readonly entity_id: string         // stringified bigint
  readonly status: WikiPageStatus
  readonly overview_md?: string | null
  readonly body_md?: string | null
  readonly is_developing_override?: boolean | null
}

/**
 * PATCH /v2/staging/wiki/entity-pages/:id request body.
 *
 * Whitelist-strict: only `title`, `slug`, `status`, `overview_md`,
 * `body_md`, `is_developing_override`, and `idempotency_key` are
 * recognized. Other fields (including `entity_id`) are silently
 * ignored so a crafted body can't surreptitiously rebind the page to
 * a different entity — create-time binding is the CP10 invariant.
 *
 * Fields omitted from the body are left untouched at the DB layer.
 * `idempotency_key` is always required; the loader emits
 * `empty_patch` when the body carries only the idempotency key and
 * no editable field.
 */
export interface WikiEntityPageEditRequest {
  readonly idempotency_key: string
  readonly title?: string
  readonly slug?: string
  readonly status?: WikiPageStatus
  readonly overview_md?: string | null
  readonly body_md?: string | null
  readonly is_developing_override?: boolean | null
}

/**
 * Row snapshot shape written to `mutation_events.new_row_json` and
 * `mutation_events.prior_row_json`, and echoed back in
 * `WikiMutationReceiptBody.row`.
 *
 * Narrower than the read-side `WikiEntityBody` (which carries
 * join-derived fields like `participations` that don't belong in an
 * audit snapshot). Column coverage matches exactly the editable /
 * computed surface on `wiki_pages`:
 *   - id / slug / title / entity_id / status /
 *     overview_md / body_md / is_developing_override — direct columns.
 *   - is_developing — computed via `wiki_pages_is_developing(wiki_pages)`
 *     in the SELECT that rehydrates the row.
 *   - created_at / updated_at — timestamps.
 */
export interface WikiPageRowSnapshot {
  readonly id: string                       // stringified bigint
  readonly slug: string
  readonly title: string
  readonly entity_id: string | null         // stringified bigint
  readonly status: WikiPageStatus
  readonly overview_md: string | null
  readonly body_md: string | null
  readonly is_developing_override: boolean | null
  readonly is_developing: boolean           // computed SQL function output
  readonly created_at: string               // ISO 8601
  readonly updated_at: string               // ISO 8601
}

/**
 * Receipt body returned on a successful wiki mutation. Wrapped in the
 * standard provenance envelope.
 *
 *   - `target_table` is always `'wiki_pages'` in Phase 1. A Phase 2
 *     arc summary edit will emit `'arcs'`; a challenge summary edit
 *     will emit `'challenges'`. Receivers that narrow on this field
 *     must handle the forward-declared values.
 *   - `target_id` is the stringified bigint id of the affected row
 *     (wiki_pages.id in Phase 1).
 *   - `audit_mirror_deferred` — true when the DB transaction committed
 *     but the post-commit R2 audit-artifact write failed. The DB is
 *     the authority. A future sweep job detects rows whose
 *     `mutation_events.artifact_key IS NULL` and retries the mirror.
 *   - `artifact_key` — R2 key of the audit artifact on successful
 *     mirror; null when the mirror deferred.
 *   - `row` carries the post-mutation snapshot (for create: the
 *     newly-inserted row; for update: the row after the update).
 *     Phase 2 extends this field into a discriminated union by
 *     `target_table` — Phase 1 handlers only emit `WikiPageRowSnapshot`.
 */
/**
 * Receipt body — generic over the row snapshot type so Phase 2's
 * arc-summary and challenge-summary edit routes can reuse the same
 * envelope shape while carrying their own narrow row type. The
 * default type parameter is `WikiPageRowSnapshot`, which keeps every
 * Phase 1 call site and every `WikiMutationReceiptBody` reference
 * that omits a type argument byte-identical in behavior.
 */
export interface WikiMutationReceiptBody<
  TRow extends
    | WikiPageRowSnapshot
    | WikiArcSummaryRowSnapshot
    | WikiChallengeSummaryRowSnapshot = WikiPageRowSnapshot,
> {
  readonly mutation_event_id: string        // stringified bigint
  readonly target_table: WikiMutationTarget
  readonly target_id: string                // stringified bigint
  readonly action: 'create' | 'update'
  readonly audit_mirror_deferred: boolean
  readonly artifact_key: string | null
  readonly row: TRow
  /**
   * Prior-row snapshot. `null` on create. Full row on update. Same
   * shape as `row`. Surfaced so the admin UI can render a diff view
   * without a second fetch.
   */
  readonly prior_row: TRow | null
}

export type WikiMutationReceiptEnvelope<
  TRow extends
    | WikiPageRowSnapshot
    | WikiArcSummaryRowSnapshot
    | WikiChallengeSummaryRowSnapshot = WikiPageRowSnapshot,
> = Envelope<WikiMutationReceiptBody<TRow>>

/**
 * Discriminated union of typed validation errors surfaced by the CP10
 * wiki mutation routes. Wire shape at HTTP 400:
 *   { ok: false, error: WikiValidationError }
 *
 * Do NOT add a free-form string variant — every 400 must be a named
 * code so the admin UI can render the error inline under the
 * offending field.
 *
 * Code vocabulary:
 *   - invalid_json                  — request body is not a JSON object
 *   - invalid_uuid                  — idempotency_key not a uuid v4
 *   - idempotency_key_in_use        — key collides with a mutation on a
 *                                     different (target_table, action,
 *                                     target_id) tuple
 *   - missing_field                 — required create field absent
 *   - invalid_type                  — wrong primitive type for a field
 *   - invalid_enum                  — `status` not in allowed values
 *   - field_too_long / field_too_short — length-cap violations
 *   - invalid_slug                  — slug fails WIKI_SLUG_RE
 *   - slug_conflict                 — remap from Postgres 23505 on
 *                                     `wiki_pages_slug_key`
 *   - invalid_entity_id             — not a positive stringified bigint
 *   - entity_not_found              — `entity_id` doesn't exist in
 *                                     `entities` (pre-INSERT check)
 *   - entity_already_has_page       — one-per-entity rule violated
 *                                     (remap from SELECT-FOR-UPDATE
 *                                     pre-check, not an FK)
 *   - wiki_page_not_found           — PATCH :id doesn't resolve
 *   - empty_patch                   — PATCH body has idempotency_key
 *                                     but no editable field
 *   - db_unavailable                — infra-level remap (network /
 *                                     pool exhaustion)
 *
 * Phase 2 extends this union with arc-summary / challenge-summary
 * variants (arc_not_found, challenge_not_found, summary_too_long).
 */
export type WikiValidationError =
  | { code: 'invalid_json' }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'idempotency_key_in_use' }
  | { code: 'missing_field'; field: keyof WikiEntityPageCreateRequest }
  | { code: 'invalid_type';  field: string; expected: string }
  | { code: 'invalid_enum';  field: 'status'; allowed: readonly WikiPageStatus[] }
  | { code: 'field_too_long'; field: 'title' | 'slug' | 'overview_md' | 'body_md' | 'summary_md'; max: number }
  | { code: 'field_too_short'; field: 'title' | 'slug'; min: number }
  | { code: 'invalid_slug'; field: 'slug' }
  | { code: 'slug_conflict'; field: 'slug' }
  | { code: 'invalid_entity_id'; field: 'entity_id' }
  | { code: 'entity_not_found'; field: 'entity_id' }
  | { code: 'entity_already_has_page'; field: 'entity_id' }
  | { code: 'wiki_page_not_found' }
  | { code: 'empty_patch' }
  | { code: 'db_unavailable' }
  // ── Phase 2 additions: arc summary + challenge summary edit ──
  | { code: 'arc_not_found' }
  | { code: 'challenge_not_found' }
  | { code: 'summary_too_long'; field: 'summary_md'; max: number }

/**
 * Error response envelope for 403 CSRF failures on CP10 routes. Same
 * five pinned reason strings as CP8 (delegated to `assertAdminCsrf`).
 */
export interface WikiMutationCsrfError {
  readonly ok: false
  readonly error: {
    readonly code: 'csrf_failed'
    readonly reason: string
  }
}

/**
 * Error response envelope for 400 validation failures on CP10 routes.
 * The admin SPA narrows on `error.code` to pick the right inline UI.
 */
export interface WikiMutationValidationErrorEnvelope {
  readonly ok: false
  readonly error: WikiValidationError
}

/**
 * R2 audit artifact body — one object per wiki mutation. Key
 * convention:
 *   ${R2_PREFIX_V2_STAGING}mutations/wiki_pages/${id}/${idempotency_key}.json
 *
 * Self-contained: carries the prior+new row snapshots so a reader of
 * the R2 artifact doesn't need to join back to `mutation_events`.
 * No PII — `actor_cookie_hash` is SHA-256 hex of the admin cookie.
 */
export interface WikiAuditArtifactBody<
  TRow extends
    | WikiPageRowSnapshot
    | WikiArcSummaryRowSnapshot
    | WikiChallengeSummaryRowSnapshot = WikiPageRowSnapshot,
> {
  readonly target_table: WikiMutationTarget
  readonly target_id: string
  readonly action: 'create' | 'update'
  readonly prior_row: TRow | null
  readonly new_row: TRow
  readonly mutation_event_id: string
  readonly actor_cookie_hash: string        // sha256 hex, 64 chars
  readonly idempotency_key: string          // uuid v4
}

export type WikiAuditArtifact<
  TRow extends
    | WikiPageRowSnapshot
    | WikiArcSummaryRowSnapshot
    | WikiChallengeSummaryRowSnapshot = WikiPageRowSnapshot,
> = Envelope<WikiAuditArtifactBody<TRow>>

// ── Wiki mutation (CP10 Phase 2) ───────────────────────────────────────
//
// Slice 2 Checkpoint 10 — Phase 2 scope:
//   - Arc summary and challenge summary edit routes. Both edit the
//     `summary_md` text column on the existing `arcs` / `challenges`
//     tables (see migrations 0002 / 0003). No schema migration is
//     needed.
//   - Two new PATCH routes, each narrow-whitelist (summary_md only):
//       PATCH /v2/staging/wiki/arc-summaries/:id
//         → WikiMutationReceiptEnvelope<WikiArcSummaryRowSnapshot>
//       PATCH /v2/staging/wiki/challenge-summaries/:id
//         → WikiMutationReceiptEnvelope<WikiChallengeSummaryRowSnapshot>
//   - Receipt shape reused (`WikiMutationReceiptBody<TRow>` is generic
//     with a default of `WikiPageRowSnapshot`; Phase 1 call sites are
//     unaffected). Audit artifact shape reused likewise.
//   - WikiValidationError extended with `arc_not_found`,
//     `challenge_not_found`, `summary_too_long` (appended above).
//
// Phase 2 does NOT extend `mutation_events.target_table` to new values
// (the column is already free-form TEXT); no DB migration.

/**
 * PATCH /v2/staging/wiki/arc-summaries/:id request body.
 *
 * Whitelist-strict: only `summary_md` (nullable) and `idempotency_key`
 * are recognized. `title`, `status`, `slug`, and every other column on
 * `arcs` are out of scope for CP10 — a future "entity / arc /
 * challenge metadata edit" surface will own those fields. Phase 2 is
 * narrow by design.
 *
 * `summary_md` is nullable: passing `null` clears the column; omitting
 * it is rejected as `empty_patch` (a PATCH must actually mutate).
 */
export interface WikiArcSummaryEditRequest {
  readonly idempotency_key: string          // uuid v4
  readonly summary_md?: string | null
}

/**
 * PATCH /v2/staging/wiki/challenge-summaries/:id request body.
 *
 * Same whitelist posture as the arc variant: only `summary_md` is
 * editable.
 */
export interface WikiChallengeSummaryEditRequest {
  readonly idempotency_key: string          // uuid v4
  readonly summary_md?: string | null
}

/**
 * Row snapshot written to `mutation_events.new_row_json` /
 * `mutation_events.prior_row_json` and echoed back in the arc summary
 * edit receipt. Narrow shape — we carry only the columns that Phase 2
 * could conceivably need to reason about (id, slug, title, status,
 * summary_md, timestamps). Fields that are out-of-scope for Phase 2
 * editing (arc_type, display_order, dates, winner_entity_id) are NOT
 * included in the snapshot to keep audit payloads compact and to
 * avoid leaking fields that aren't part of this mutation's
 * before/after story.
 */
export interface WikiArcSummaryRowSnapshot {
  readonly id: string                       // stringified bigint
  readonly slug: string
  readonly title: string
  readonly status: string                   // arc_status enum as string
  readonly summary_md: string | null
  readonly created_at: string               // ISO 8601
  readonly updated_at: string               // ISO 8601
}

/**
 * Row snapshot for challenge summary edits. Narrow for the same
 * reason as `WikiArcSummaryRowSnapshot`.
 */
export interface WikiChallengeSummaryRowSnapshot {
  readonly id: string                       // stringified bigint
  readonly arc_id: string                   // stringified bigint
  readonly title: string
  readonly summary_md: string | null
  readonly created_at: string               // ISO 8601
  readonly updated_at: string               // ISO 8601
}

// -- Recap-event review-state mutation (CP11 Phase 1) -------------------
//
// Slice 2 Checkpoint 11 — first slice scope:
//   - Closes the editorial control loop on `recap_events` by adding a
//     review-state mutation route, the missing public-visibility
//     filter, and minimal admin row controls.
//
//   - One new mutation route:
//       PATCH /v2/staging/recap-events/:id/review-state
//         → RecapEventReviewStateChangeReceipt
//
//   - Reuses the CP8 mutation template byte-identically:
//     `target_table='recap_events'`, `action='update'` (no new verb),
//     same CSRF / idempotency / audit / R2 mirror posture. The new
//     CSRF action label is `'recap-event-review-state-write'`.
//
//   - Optional `reason` field captured ONLY in the audit body — the
//     `recap_events` table has no `hide_reason` column and CP11 does
//     not add one (migration-free slice).
//
//   - The DB trigger `enforce_published_at` is the single source of
//     truth for `published_at`; the loader does not stamp it directly.
//     The receipt returns the post-trigger row so callers see the new
//     `published_at` immediately.

/**
 * Mirror of the DB enum `recap_review_state` values, in the order the
 * admin UI surfaces them. Mirrors `RecapReviewState` (declared above).
 * Exported as a `const` array so the parser and the admin SPA can
 * iterate the canonical set without duplicating the literals.
 */
export const RECAP_REVIEW_STATES = [
  'unreviewed',
  'approved',
  'hidden',
  'flagged',
] as const

/**
 * PATCH /v2/staging/recap-events/:id/review-state request body.
 *
 * `to_state` must be one of `RECAP_REVIEW_STATES`. The Worker maps
 * an unknown value to `invalid_review_state`.
 *
 * `reason` is optional and persisted ONLY in the audit body
 * (`mutation_events.new_row_json`'s `kind:'recap_event_review_state_change'`
 * variant). The `recap_events` content row is unchanged except for
 * `review_state` (and `published_at`, which the DB trigger fills on
 * flip-to-`'approved'`). CP11 does not enforce a `reason` requirement
 * server-side; the admin UI prompts for it on `'hidden'` / `'flagged'`
 * by convention.
 *
 * `idempotency_key` is a client-generated UUIDv4. Same UNIQUE
 * collision semantics as CP8/CP10:
 *   - Replay with same key + same `to_state` → prior receipt verbatim.
 *   - Replay with same key + different `to_state` → 409 conflict.
 *
 * Same-state PATCH (current `review_state` already equals `to_state`)
 * surfaces `review_state_unchanged` 422 — operator no-op, no audit
 * row written.
 */
export interface RecapEventReviewStateChangeRequest {
  readonly idempotency_key: string
  readonly to_state: RecapReviewState
  readonly reason?: string | null
}

/**
 * Audit body variant written to `mutation_events.new_row_json` when a
 * review-state PATCH succeeds. Discriminated by `kind`. Carries the
 * pre/post state pair, optional reason, and the actor cookie hash.
 *
 * Notable absences vs the CP8 `RecapEventAuditArtifactBody`:
 *   - No `prior_row` / `new_row` snapshots — the only column that
 *     changes is `review_state` (and `published_at` if the trigger
 *     fires), and those are derivable from `from_state` / `to_state`
 *     plus the trigger semantics. Keeping the audit body narrow lets
 *     a future audit-history reader render a compact "approve / hide /
 *     flag / unreview" timeline.
 *   - `target_table` pinned to the `recap_events` literal so a future
 *     surface that reviews other tables emits a different variant.
 */
export interface RecapEventReviewStateAuditBody {
  readonly kind: 'recap_event_review_state_change'
  readonly target_table: 'recap_events'
  readonly target_id: string                // stringified bigint
  readonly from_state: RecapReviewState
  readonly to_state: RecapReviewState
  readonly reason: string | null
  readonly actor_cookie_hash: string
  readonly mutation_event_id: string
  readonly idempotency_key: string
}

/**
 * Receipt body returned on a successful review-state flip. Same
 * provenance envelope wrapping as CP8/CP10. The `row` field carries
 * the post-trigger `RecapEventForAdmin` snapshot so the admin SPA
 * sees the new `published_at` immediately on approve.
 *
 * `prior_row` carries the pre-flip snapshot.
 *
 * `audit_mirror_deferred` follows the CP8/CP10 contract: only present
 * (and only `true`) when the DB transaction committed but the R2
 * audit-artifact write failed.
 */
export interface RecapEventReviewStateChangeReceiptBody {
  readonly row: RecapEventForAdmin
  readonly prior_row: RecapEventForAdmin
  readonly mutation_event_id: string
  readonly from_state: RecapReviewState
  readonly to_state: RecapReviewState
  readonly reason: string | null
  audit_mirror_deferred?: boolean
}

export type RecapEventReviewStateChangeReceipt =
  Envelope<RecapEventReviewStateChangeReceiptBody>

/**
 * Validation-error variants specific to the review-state route. These
 * extend the request-shape errors that already exist in
 * `RecapEventValidationError` (the worker reuses the existing
 * `invalid_uuid` / `invalid_enum` codes for shape-level issues; the
 * two below are the route-specific additions).
 *
 * Two new codes:
 *   - `invalid_review_state` — `to_state` is not one of
 *     `RECAP_REVIEW_STATES`. Carries the offending value so the admin
 *     UI can surface it.
 *   - `review_state_unchanged` — the row's current `review_state`
 *     already equals `to_state`. This is an operator no-op; we surface
 *     it as 422 rather than silently 200 so the admin SPA can show a
 *     gentle "already in that state" toast and not write an audit row.
 *
 * Wire shape: surfaced inside the standard
 * `{ ok: false, error: <variant> }` envelope.
 */
export type RecapEventReviewStateValidationError =
  | { code: 'invalid_review_state'; field: 'to_state'; received: string }
  | { code: 'review_state_unchanged'; field: 'to_state'; current: RecapReviewState }
  | { code: 'idempotency_conflict'; field: 'idempotency_key'; prior_to_state: RecapReviewState; requested_to_state: RecapReviewState }
  | { code: 'invalid_enum'; field: string; allowed: string[] }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'not_found' }

// =========================================================================
// CP12 — wiki-page status mutation (Phase 1, entity wiki pages only)
// Additive only: every export above this banner stays byte-identical.
// Mirrors the CP11 review-state shape so the admin SPA, the audit
// reader, and the receipt envelope all follow one pattern.
// =========================================================================
//
// Slice 2 Checkpoint 12 — first slice scope:
//   - Closes the editorial control loop on entity wiki pages by adding
//     a status mutation route, the missing public-visibility filter on
//     `loadWikiEntityBody`, and minimal admin row controls.
//
//   - One new mutation route:
//       PATCH /v2/staging/wiki/entity-pages/:id/status
//         → WikiPageStatusChangeReceipt
//
//   - Reuses the CP8/CP10 mutation template byte-identically:
//     `target_table='wiki_pages'`, `action='update'` (no new verb),
//     same CSRF / idempotency / audit / R2 mirror posture. The new
//     CSRF action label is `'wiki-page-status-write'`.
//
//   - Optional `reason` field captured ONLY in the audit body — the
//     `wiki_pages` table has no reason column and CP12 does not add
//     one (migration-free slice).
//
//   - The status column already exists on `wiki_pages` with a CHECK
//     constraint enforcing the four enum values. CP12 layers an
//     allowed-transition graph on top of the CHECK to forbid e.g.
//     `archived → published` direct flips.
//
//   - Phase 1 covers entity wiki pages ONLY. Arcs and challenges have
//     no review_state column and are intentionally out of scope; their
//     review controls are deferred to a future slice.

/**
 * Allowed status transitions for entity wiki pages. The graph is
 * intentionally narrow: every arrow reflects an editorial action the
 * admin SPA has a button for. Same-state PATCHes are a no-op surfaced
 * as `wiki_page_status_unchanged` 422.
 *
 *   draft     → review | published | archived
 *   review    → draft | published | archived
 *   published → draft | review | archived
 *   archived  → draft
 *
 * Rationale: the public reader filters on `status='published'`. Any
 * transition that affects public visibility goes through this graph,
 * and `archived → draft` is the single lane back from the terminal
 * state (no direct unarchive-to-published, to force a re-review pass).
 */
export const WIKI_PAGE_STATUS_TRANSITIONS: Readonly<Record<WikiPageStatus, readonly WikiPageStatus[]>> = {
  draft:     ['review', 'published', 'archived'],
  review:    ['draft', 'published', 'archived'],
  published: ['draft', 'review', 'archived'],
  archived:  ['draft'],
} as const

/**
 * PATCH /v2/staging/wiki/entity-pages/:id/status request body.
 *
 * `to_status` must be one of `WikiPageStatus`. The Worker maps an
 * unknown value to `invalid_to_status`. A status that is not in the
 * `WIKI_PAGE_STATUS_TRANSITIONS[from_status]` set surfaces
 * `wiki_status_transition_forbidden`.
 *
 * `reason` is optional and persisted ONLY in the audit body
 * (`mutation_events.new_row_json`'s
 * `kind:'wiki_page_status_change'` variant). The `wiki_pages` content
 * row is unchanged except for `status`. CP12 does not enforce a
 * `reason` requirement server-side; the admin UI prompts for one on
 * `archived` by convention.
 *
 * `idempotency_key` is a client-generated UUIDv4. Same UNIQUE
 * collision semantics as CP8/CP10/CP11:
 *   - Replay with same key + same `to_status` → prior receipt verbatim.
 *   - Replay with same key + different `to_status` → 409 conflict.
 *
 * Same-status PATCH (current `status` already equals `to_status`)
 * surfaces `wiki_page_status_unchanged` 422 — operator no-op, no audit
 * row written.
 */
export interface WikiPageStatusChangeRequest {
  readonly idempotency_key: string
  readonly to_status: WikiPageStatus
  readonly reason?: string | null
}

/**
 * Audit body variant written to `mutation_events.new_row_json` when a
 * wiki-page status PATCH succeeds. Discriminated by `kind`. Carries
 * the pre/post status pair, optional reason, and the actor cookie
 * hash.
 *
 * Notable absences vs the CP10 `WikiAuditArtifactBody`:
 *   - No `prior_row` / `new_row` snapshots — the only column that
 *     changes is `status`, which is fully captured in
 *     `from_status` / `to_status`. Keeping the audit body narrow lets
 *     a future audit-history reader render a compact "draft → review →
 *     published" timeline.
 *   - `target_table` pinned to the `wiki_pages` literal so a future
 *     surface that reviews other tables (arcs, challenges) emits a
 *     different variant.
 */
export interface WikiPageStatusChangeAuditBody {
  readonly kind: 'wiki_page_status_change'
  readonly target_table: 'wiki_pages'
  readonly target_id: string                // stringified bigint
  readonly from_status: WikiPageStatus
  readonly to_status: WikiPageStatus
  readonly reason: string | null
  readonly actor_cookie_hash: string
  readonly mutation_event_id: string
  readonly idempotency_key: string
}

/**
 * Receipt body returned on a successful wiki-page status flip. Same
 * provenance envelope wrapping as CP8/CP10/CP11. The `row` field
 * carries the post-update `WikiPageRowSnapshot` so the admin SPA sees
 * the new `status` immediately.
 *
 * `prior_row` carries the pre-flip snapshot.
 *
 * `audit_mirror_deferred` follows the CP8/CP10/CP11 contract: only
 * present (and only `true`) when the DB transaction committed but the
 * R2 audit-artifact write failed.
 */
export interface WikiPageStatusChangeReceiptBody {
  readonly row: WikiPageRowSnapshot
  readonly prior_row: WikiPageRowSnapshot
  readonly mutation_event_id: string
  readonly from_status: WikiPageStatus
  readonly to_status: WikiPageStatus
  readonly reason: string | null
  audit_mirror_deferred?: boolean
}

export type WikiPageStatusChangeReceipt =
  Envelope<WikiPageStatusChangeReceiptBody>

/**
 * Validation-error variants specific to the wiki-page status route.
 * These extend the request-shape errors that already exist in
 * `WikiValidationError` (the worker reuses the existing
 * `invalid_uuid` / `invalid_enum` codes for shape-level issues; the
 * variants below are the route-specific additions).
 *
 * New codes:
 *   - `invalid_to_status` — `to_status` is not one of the
 *     `WikiPageStatus` literals. Carries the offending value so the
 *     admin UI can surface it.
 *   - `wiki_page_status_unchanged` — the row's current `status`
 *     already equals `to_status`. Operator no-op surfaced as 422 so
 *     the admin SPA can show a gentle "already in that status" toast
 *     and not write an audit row.
 *   - `wiki_status_transition_forbidden` — the requested transition
 *     is not in `WIKI_PAGE_STATUS_TRANSITIONS[from_status]`. Carries
 *     both endpoints so the admin UI can render an actionable message.
 *   - `wiki_page_status_idempotency_conflict` — same idempotency key
 *     was previously used with a different `to_status`. Distinct from
 *     the generic `idempotency_key_in_use` so the admin SPA can show
 *     the prior-vs-requested pair.
 *
 * Wire shape: surfaced inside the standard
 * `{ ok: false, error: <variant> }` envelope.
 */
export type WikiPageStatusValidationError =
  | { code: 'invalid_to_status'; field: 'to_status'; received: string }
  | { code: 'wiki_page_status_unchanged'; field: 'to_status'; current: WikiPageStatus }
  | { code: 'wiki_status_transition_forbidden'; field: 'to_status'; from_status: WikiPageStatus; to_status: WikiPageStatus }
  | { code: 'wiki_page_status_idempotency_conflict'; field: 'idempotency_key'; prior_to_status: WikiPageStatus; requested_to_status: WikiPageStatus }
  | { code: 'invalid_enum'; field: string; allowed: string[] }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'wiki_page_not_found' }

// =========================================================================
// CP13 — TTS entry review-state mutation (Phase 1, tts_entries only)
// Additive only: every export above this banner stays byte-identical.
// Mirrors the CP11 review-state shape so the admin SPA, the audit
// reader, and the receipt envelope all follow one pattern.
// =========================================================================
//
// Slice 2 Checkpoint 13 — Phase 1 scope:
//   - Closes the editorial control loop on `tts_entries` by adding a
//     review-state mutation route, an admin-queue read variant of the
//     existing public list, and minimal admin row controls. The public
//     reader (`/v2/staging/tts/list.json`) is byte-identical to CP6.
//
//   - One new mutation route:
//       PATCH /v2/staging/tts/entries/:id/review-state
//         → TtsEntryReviewStateChangeReceipt
//
//   - One new admin-only read route:
//       GET  /v2/staging/tts/admin/queue.json
//         → TtsAdminQueueArtifact
//
//   - Reuses the CP11 mutation template byte-for-byte: same CSRF /
//     idempotency / two-step audit / R2 mirror posture. The new CSRF
//     action label is `'tts-entry-review-state-write'`.
//
//   - `tts_entries` carries BOTH the shared `recap_review_state` enum
//     AND a separate `hide_reason TEXT NULL` column. CP13 writes both
//     in the same transaction:
//       - `to_state ∈ {'hidden','flagged'}` → `hide_reason := request.reason`
//         (NULL is allowed; operator may flag without a reason).
//       - `to_state ∈ {'approved','unreviewed'}` → `hide_reason := NULL`
//         (cleared, regardless of request.reason — the public reader's
//         belt-and-suspenders filter relies on this column being null
//         for visible rows).
//     The audit body captures BOTH the review_state pair AND the
//     hide_reason pair so a future audit-history reader can render a
//     full timeline including the reason changes.
//
//   - Phase 1 is `tts_entries` only. `production_messages` and
//     `x_source_entries` have parallel `review_state` columns and are
//     intentionally out of scope; their review controls are deferred
//     to a future slice.

/**
 * PATCH /v2/staging/tts/entries/:id/review-state request body.
 *
 * `to_state` must be one of `RECAP_REVIEW_STATES` (the enum is shared
 * across recap_events / tts_entries / production_messages /
 * x_source_entries — see `RecapReviewState` above). The Worker maps
 * an unknown value to `invalid_review_state`.
 *
 * `reason` is optional and persisted in two places when supplied:
 *   - The audit body (`mutation_events.new_row_json`'s
 *     `kind:'tts_entry_review_state_change'` variant), unconditionally.
 *   - The `tts_entries.hide_reason` column, ONLY when `to_state` is
 *     `'hidden'` or `'flagged'`. On transitions to `'approved'` or
 *     `'unreviewed'` the column is cleared to NULL regardless of the
 *     request `reason`.
 *
 * `idempotency_key` is a client-generated UUIDv4. Same UNIQUE
 * collision semantics as CP8/CP10/CP11/CP12:
 *   - Replay with same key + same `to_state` → prior receipt verbatim.
 *   - Replay with same key + different `to_state` → 409 conflict.
 *
 * Same-state PATCH (current `review_state` already equals `to_state`)
 * surfaces `tts_review_state_unchanged` 422 — operator no-op, no audit
 * row written. The hide_reason column is NOT considered in the
 * unchanged check; an operator who wants to update only the reason on
 * an already-hidden row should issue a flip away and back, or use a
 * future dedicated reason-edit route (out of scope for Phase 1).
 */
export interface TtsEntryReviewStateChangeRequest {
  readonly idempotency_key: string
  readonly to_state: RecapReviewState
  readonly reason?: string | null
}

/**
 * Audit body variant written to `mutation_events.new_row_json` when a
 * TTS review-state PATCH succeeds. Discriminated by `kind`. Carries
 * the pre/post review_state pair, the pre/post hide_reason pair, the
 * operator-supplied reason, and the actor cookie hash.
 *
 * Notable shape vs `RecapEventReviewStateAuditBody`:
 *   - Adds `from_hide_reason` / `to_hide_reason` because tts_entries
 *     mutates that column alongside review_state (see write-through
 *     rule on `TtsEntryReviewStateChangeRequest`). Recap_events has
 *     no equivalent column, hence its audit body omits the pair.
 *   - `target_table` pinned to the `tts_entries` literal so a future
 *     surface that reviews production_messages or x_source_entries
 *     emits a different variant.
 *
 * `reason` is the operator-supplied reason as received in the request,
 * preserved verbatim (modulo trim/length cap). `to_hide_reason` is the
 * value actually written to the column after the write-through rule
 * runs — these can differ: e.g. flipping to `'approved'` with a
 * `reason` set will store `reason` in the audit body but
 * `to_hide_reason: null` in the column.
 */
export interface TtsEntryReviewStateAuditBody {
  readonly kind: 'tts_entry_review_state_change'
  readonly target_table: 'tts_entries'
  readonly target_id: string                // stringified bigint
  readonly from_state: RecapReviewState
  readonly to_state: RecapReviewState
  readonly from_hide_reason: string | null
  readonly to_hide_reason: string | null
  readonly reason: string | null
  readonly actor_cookie_hash: string
  readonly mutation_event_id: string
  readonly idempotency_key: string
}

/**
 * Admin queue row. Same row vocabulary as `TtsListItem` (so the admin
 * SPA can reuse the public renderer's row shape) plus the two columns
 * the editorial workflow actually mutates: `review_state` and
 * `hide_reason`. The admin queue is unfiltered by review_state — it
 * shows every row regardless of state so an operator can flip a
 * mistakenly-approved row back to `'flagged'` without first having to
 * re-find it. Pagination cap mirrors the public list.
 */
export interface TtsAdminQueueItem {
  id: string
  body: string
  voice: string | null
  spoken_at: string | null
  tts_source: 'live_stream' | 'archive' | 'manual'
  review_state: RecapReviewState
  hide_reason: string | null
  arc: LiveArcRef | null
  entities: RecapEventEntityRef[]
}

/**
 * Pagination block for the admin queue. Same shape as
 * `TtsListPagination` for renderer reuse.
 */
export interface TtsAdminQueuePagination {
  page: number
  page_size: number
  total_returned: number
  has_next: boolean
  truncated: boolean
}

/**
 * Admin-queue body. No filter row in Phase 1 — `page` is the only
 * input. The body still echoes the requested page so the renderer can
 * derive prev/next without re-parsing the URL.
 */
export interface TtsAdminQueueBody {
  filters: { page: number }
  items: TtsAdminQueueItem[]
  pagination: TtsAdminQueuePagination
}

export type TtsAdminQueueArtifact = Envelope<TtsAdminQueueBody>

/**
 * Receipt body returned on a successful TTS review-state flip. Same
 * provenance envelope wrapping as CP8/CP10/CP11/CP12. The `row` field
 * carries the post-update admin row snapshot so the admin SPA sees the
 * new `review_state` AND `hide_reason` immediately on flip.
 *
 * `prior_row` carries the pre-flip snapshot, so the SPA can render a
 * compact diff or undo affordance without re-fetching.
 *
 * `audit_mirror_deferred` follows the CP8/CP10/CP11/CP12 contract:
 * only present (and only `true`) when the DB transaction committed
 * but the R2 audit-artifact write failed.
 */
export interface TtsEntryReviewStateChangeReceiptBody {
  readonly row: TtsAdminQueueItem
  readonly prior_row: TtsAdminQueueItem
  readonly mutation_event_id: string
  readonly from_state: RecapReviewState
  readonly to_state: RecapReviewState
  readonly from_hide_reason: string | null
  readonly to_hide_reason: string | null
  readonly reason: string | null
  audit_mirror_deferred?: boolean
}

export type TtsEntryReviewStateChangeReceipt =
  Envelope<TtsEntryReviewStateChangeReceiptBody>

/**
 * Validation-error variants specific to the TTS review-state route.
 * Mirrors the CP11 `RecapEventReviewStateValidationError` shape
 * one-for-one (same code names except for the `_unchanged` and
 * `not_found` discriminators, which are namespaced to the table so an
 * admin SPA renderer can pattern-match without inspecting the route).
 *
 * Codes:
 *   - `invalid_review_state` — `to_state` is not one of
 *     `RECAP_REVIEW_STATES`. Carries the offending value.
 *   - `tts_review_state_unchanged` — the row's current `review_state`
 *     already equals `to_state`. Operator no-op surfaced as 422 so the
 *     admin SPA can show a gentle "already in that state" toast and
 *     not write an audit row.
 *   - `tts_idempotency_conflict` — same idempotency key was previously
 *     used with a different `to_state`. Distinct from the generic
 *     `idempotency_key_in_use` so the admin SPA can show the
 *     prior-vs-requested pair.
 *   - `invalid_enum` / `invalid_uuid` — shape errors, reused.
 *   - `tts_entry_not_found` — no row matches the path id (or it has
 *     been hard-deleted between list fetch and PATCH).
 *
 * Wire shape: surfaced inside the standard
 * `{ ok: false, error: <variant> }` envelope.
 */
export type TtsEntryReviewStateValidationError =
  | { code: 'invalid_review_state'; field: 'to_state'; received: string }
  | { code: 'tts_review_state_unchanged'; field: 'to_state'; current: RecapReviewState }
  | { code: 'tts_idempotency_conflict'; field: 'idempotency_key'; prior_to_state: RecapReviewState; requested_to_state: RecapReviewState }
  | { code: 'invalid_enum'; field: string; allowed: string[] }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'tts_entry_not_found' }

// -- Slice 4: TTS entry hard delete -------------------------------------
//
// Mirrors the slice 3 RecapEventDelete* shapes — whitelist-strict
// body, typed receipt with `deleted_row`, closed validation-error
// union. The audit body for the delete uses a separate discriminated
// `kind: 'tts_entry_delete'` variant so it doesn't collide with the
// existing `tts_entry_review_state_change` audit shape.
//
// FK posture (migration 0005): `tts_entry_entities.tts_entry_id` is
// `ON DELETE CASCADE`. No application-level child sweep is required.

/**
 * Whitelist-strict DELETE body. Just the `idempotency_key`. Mirrors
 * RecapEventDeleteRequest verbatim.
 */
export interface TtsEntryDeleteRequest {
  idempotency_key: string
}

/**
 * Closed validation-error union for the TTS delete surface.
 * Distinct from `TtsEntryReviewStateValidationError` so the SPA
 * narrows on a tighter set: delete has no `to_state` / `reason`
 * shape, no `_unchanged` / `_idempotency_conflict` states.
 *
 * `tts_entry_not_found` is reused from the review-state union for
 * symmetry with the review-state 404 — a hard-deleted row and a
 * never-existed row both surface as the same code.
 */
export type TtsEntryDeleteValidationError =
  | { code: 'invalid_body' }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'idempotency_key_in_use'; field: 'idempotency_key' }
  | { code: 'tts_entry_not_found' }

/**
 * Receipt body returned on successful delete. Carries the full
 * snapshot of the deleted admin queue row so the SPA can render an
 * undo / "deleted X" toast without a re-fetch. Same
 * `audit_mirror_deferred` posture as every other admin mutation.
 */
export interface TtsEntryDeleteReceiptBody {
  /** Full snapshot of the deleted row in admin queue shape. */
  deleted_row: TtsAdminQueueItem
  /** stringified bigint — the `mutation_events` row id. */
  mutation_event_id: string
  audit_mirror_deferred?: true
}

export type TtsEntryDeleteReceipt =
  Envelope<TtsEntryDeleteReceiptBody>

/**
 * Audit body for a TTS hard delete, persisted to
 * `mutation_events.prior_row_json` and to the R2 mirror artifact.
 * Distinct discriminated `kind` from the review-state change so
 * a single read of the JSON tells the consumer which mutation
 * type produced it. `new_row_json` is NULL on delete (per
 * migration 0009 + the established slice-3 posture).
 */
export interface TtsEntryDeleteAuditBody {
  readonly kind: 'tts_entry_delete'
  readonly target_table: 'tts_entries'
  readonly target_id: string
  readonly deleted_row: TtsAdminQueueItem
  readonly actor_cookie_hash: string
  readonly mutation_event_id: string
  readonly idempotency_key: string
}

// =========================================================================
// CP14 — Production-messages review-state mutation (Phase 1, production_messages only)
// Additive only: every export above this banner stays byte-identical.
// Mirrors the CP13 shape verbatim, substituting tts_entries →
// production_messages. Two structural differences from CP13:
//   (a) admin queue item OMITS `entities[]` — no
//       `production_message_entities` link table exists at the schema
//       level (see lib/production-messages-loader.ts:17-20 and the
//       CP14 audit §0). This is intentional.
//   (b) admin queue parser is case-preserving on `?author=`, mirroring
//       the CP7 public-loader rule (see audit P16); the admin queue
//       item's `author` field is therefore rendered exactly as stored.
// =========================================================================
//
// Slice 2 Checkpoint 14 — Phase 1 scope:
//   - Closes the editorial control loop on `production_messages` by
//     adding a review-state mutation route, an admin-queue read variant
//     of the existing public list, and minimal admin row controls.
//     The public reader (`/v2/staging/production-messages/list.json`)
//     is byte-identical to CP7.
//
//   - One new mutation route:
//       PATCH /v2/staging/production-messages/:id/review-state
//         → ProductionMessageReviewStateChangeReceipt
//
//   - One new admin-only read route:
//       GET  /v2/staging/production-messages/admin/queue.json
//         → ProductionMessagesAdminQueueArtifact
//
//   - Reuses the CP13 mutation template byte-for-byte: same CSRF /
//     idempotency / two-step audit / R2 mirror posture. The new CSRF
//     action label is `'production-message-review-state-write'`.
//
//   - `production_messages` carries BOTH the shared `recap_review_state`
//     enum AND a separate `hide_reason TEXT NULL` column. CP14 writes
//     both in the same transaction, identically to CP13:
//       - `to_state ∈ {'hidden','flagged'}` → `hide_reason := request.reason`
//         (required non-empty trimmed, max 1024 chars).
//       - `to_state ∈ {'approved','unreviewed'}` → `hide_reason := NULL`
//         (cleared, regardless of request.reason — the public reader's
//         belt-and-suspenders filter relies on this column being NULL
//         for visible rows).
//     The audit body captures BOTH the review_state pair AND the
//     hide_reason pair so an audit-history reader can render a full
//     timeline including the reason changes.
//
//   - Phase 1 is `production_messages` only. `x_source_entries` has a
//     parallel `review_state` column and is intentionally out of scope;
//     its review controls are deferred to a future slice.

/**
 * PATCH /v2/staging/production-messages/:id/review-state request body.
 *
 * `to_state` must be one of `RECAP_REVIEW_STATES` (the enum is shared
 * across recap_events / tts_entries / production_messages /
 * x_source_entries — see `RecapReviewState` above). The Worker maps
 * an unknown value to `invalid_review_state`.
 *
 * `reason` is required (non-empty trimmed, ≤
 * `PRODUCTION_MESSAGE_REVIEW_REASON_MAX` 1024 chars) when `to_state`
 * is `'hidden'` or `'flagged'`. It is silently dropped when `to_state`
 * is `'approved'` or `'unreviewed'`; the column is cleared to NULL
 * regardless of request input. Mirrors CP13's posture.
 *
 * `idempotency_key` is a client-generated UUIDv4. Same UNIQUE
 * collision semantics as CP8/CP10/CP11/CP12/CP13:
 *   - Replay with same key + same `to_state` → prior receipt verbatim.
 *   - Replay with same key + different `to_state` → 409 conflict.
 *
 * Same-state PATCH (current `review_state` already equals `to_state`)
 * surfaces `production_message_review_state_unchanged` 422 — operator
 * no-op, no audit row written. The hide_reason column is NOT considered
 * in the unchanged check (identical to CP13).
 */
export interface ProductionMessageReviewStateChangeRequest {
  readonly idempotency_key: string
  readonly to_state: RecapReviewState
  readonly reason?: string | null
}

/**
 * Admin queue row. Carries the same vocabulary as
 * `ProductionMessagesListItem` (public reader) plus the two columns
 * the editorial workflow mutates (`review_state`, `hide_reason`) and
 * the ingest/audit bookkeeping columns (`source_external_id`,
 * `created_at`, `updated_at`). The admin queue is unfiltered by
 * `review_state` — it shows every row regardless of state so an
 * operator can flip a mistakenly-approved row back to `'flagged'`
 * without first having to re-find it.
 *
 * Deliberate omissions vs `TtsAdminQueueItem`:
 *   - NO `entities[]` field. The `production_messages` schema has no
 *     `production_message_entities` link table — this is a real schema
 *     constraint (see CP14 audit §0 and
 *     lib/production-messages-loader.ts:17-20). Adding an `entities`
 *     field here would force either an invented JOIN against a
 *     nonexistent table or a hardcoded empty array, both wrong.
 *   - NO `voice` column. Production messages carry `author` instead.
 *   - NO source-kind badge. `production_messages` has no
 *     `production_messages_source` enum analogous to `tts_source`;
 *     it has a single `source_external_id TEXT NOT NULL UNIQUE`.
 */
export interface ProductionMessagesAdminQueueItem {
  id: string
  source_external_id: string
  body: string
  /** Case-preserved per CP7 rule (audit P16). Rendered as stored. */
  author: string | null
  occurred_at: string | null
  arc: LiveArcRef | null
  review_state: RecapReviewState
  hide_reason: string | null
  created_at: string
  updated_at: string
}

/**
 * Pagination block for the admin queue. Shape matches
 * `ProductionMessagesListPagination` so renderers can share the pager
 * component.
 */
export interface ProductionMessagesAdminQueuePagination {
  page: number
  page_size: number
  total_returned: number
  has_next: boolean
  truncated: boolean
}

/**
 * Admin-queue body. Filters are echoed so the renderer can derive
 * prev/next and preserve the operator-typed query without re-parsing
 * the URL. `author` is case-preserved end-to-end (audit P16).
 */
export interface ProductionMessagesAdminQueueBody {
  filters: {
    since: string | null
    review_state: RecapReviewState | null
    /** Case-preserved per CP7 rule (audit P16). */
    author: string | null
    page: number
  }
  items: ProductionMessagesAdminQueueItem[]
  pagination: ProductionMessagesAdminQueuePagination
}

export type ProductionMessagesAdminQueueArtifact =
  Envelope<ProductionMessagesAdminQueueBody>

/**
 * Audit body variant written to `mutation_events.new_row_json` when a
 * production-message review-state PATCH succeeds. Discriminated by
 * `kind`. Carries the pre/post review_state pair, the pre/post
 * hide_reason pair, the operator-supplied reason, and the actor
 * cookie hash.
 *
 * `target_table` is pinned to the `production_messages` literal so a
 * future surface reviewing `x_source_entries` emits a different
 * variant with its own `kind`.
 */
export interface ProductionMessageReviewStateChangeAuditBody {
  readonly kind: 'production_message_review_state_change'
  readonly target_table: 'production_messages'
  readonly target_id: string // stringified bigint
  readonly production_message_id: string // same value as target_id, namespaced for cross-variant readability
  readonly from_state: RecapReviewState
  readonly to_state: RecapReviewState
  readonly from_hide_reason: string | null
  readonly to_hide_reason: string | null
  readonly reason: string | null
  readonly actor_cookie_hash: string
  readonly mutation_event_id: string
  readonly idempotency_key: string
}

/**
 * Receipt body returned on a successful production-message
 * review-state flip. Same provenance envelope wrapping as
 * CP8/CP10/CP11/CP12/CP13. `row` carries the post-update admin row
 * snapshot so the admin SPA sees the new `review_state` AND
 * `hide_reason` immediately on flip. `prior_row` carries the pre-flip
 * snapshot so the SPA can render a compact diff or undo affordance
 * without re-fetching.
 *
 * `audit_mirror_deferred` follows the CP8/CP10/CP11/CP12/CP13
 * contract: only present (and only `true`) when the DB transaction
 * committed but the R2 audit-artifact write failed.
 */
export interface ProductionMessageReviewStateChangeReceiptBody {
  readonly row: ProductionMessagesAdminQueueItem
  readonly prior_row: ProductionMessagesAdminQueueItem
  readonly mutation_event_id: string
  readonly from_state: RecapReviewState
  readonly to_state: RecapReviewState
  readonly from_hide_reason: string | null
  readonly to_hide_reason: string | null
  readonly reason: string | null
  audit_mirror_deferred?: boolean
}

export type ProductionMessageReviewStateChangeReceipt =
  Envelope<ProductionMessageReviewStateChangeReceiptBody>

/**
 * Validation-error variants specific to the production-message
 * review-state route. Mirrors the CP13 `TtsEntryReviewStateValidationError`
 * shape, with the `_unchanged`, `_idempotency_conflict`, and
 * `_not_found` discriminators namespaced to `production_message_*` so
 * the admin SPA renderer can pattern-match without inspecting the
 * route. Adds two hide_reason variants:
 *   - `production_message_hide_reason_required` — hide/flag target
 *     without a non-empty trimmed reason.
 *   - `production_message_hide_reason_too_long` — reason exceeds
 *     `PRODUCTION_MESSAGE_REVIEW_REASON_MAX` (1024).
 */
export type ProductionMessageReviewStateValidationError =
  | { code: 'invalid_review_state'; field: 'to_state'; received: string }
  | { code: 'production_message_review_state_unchanged'; field: 'to_state'; current: RecapReviewState }
  | { code: 'production_message_idempotency_conflict'; field: 'idempotency_key'; prior_to_state: RecapReviewState; requested_to_state: RecapReviewState }
  | { code: 'invalid_enum'; field: string; allowed: string[] }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'production_message_not_found' }
  | { code: 'production_message_hide_reason_required'; field: 'reason' }
  | { code: 'production_message_hide_reason_too_long'; field: 'reason'; max: number }

// ----------------------------------------------------------------------------
// Slice 2 Checkpoint 15 — x_source_entries review/moderation.
//
//   - CP15 closes the editorial-lifecycle slice family (recap_events,
//     tts_entries, production_messages, x_source_entries). The shared
//     `recap_review_state` enum (CP1) is reused; `x_source_entries`
//     already carries `review_state recap_review_state NOT NULL DEFAULT
//     'unreviewed'` and `hide_reason TEXT NULL` columns, so no migration
//     is added.
//
//   - One new mutation route:
//       PATCH /v2/staging/x-source-entries/:id/review-state
//         → XSourceEntryReviewStateChangeReceipt
//
//   - One new admin-only read route:
//       GET  /v2/staging/x-source-entries/admin/queue.json
//         → XSourceEntriesAdminQueueArtifact
//
//   - Structural clone of CP14 (production_messages) with column
//     substitutions: `production_message_id → x_source_entry_id`,
//     `author → author_handle`, `occurred_at → posted_at`
//     (NULLABLE — unlike `production_messages.occurred_at`); adds a
//     `platform_id` passthrough (the UNIQUE external-platform key,
//     also carried in the audit body for grep-ability).
//
//   - Strict hide_reason posture INHERITED FROM CP14: required
//     non-empty trimmed, ≤ 1024 chars on hide/flag; empty → HTTP 400;
//     overflow → HTTP 400. HTTP 422 is reserved for the single
//     no-op code `x_source_entry_review_state_unchanged`.
//
//   - CP15 deliberately OMITS (first slice):
//       - `entities[]` on the admin row (link table exists but
//         surfacing is deferred; zero lifecycle-proof benefit).
//       - a typed `payload` field (`x_source_entries.payload JSONB`
//         is opaque today — no producer, no consumer, no known
//         shape; typing is a provenance decision out of CP15 scope).
//       - the `?since=` and `?author_handle=` admin-queue filters
//         (deferred; admin queue supports `?review_state=` + `?page=`
//         only in first slice).
//       - any public reader. There is no
//         `/v2/staging/x-source-entries/list.json` route.
//         `x_source_entries` is a moderation surface, not a
//         publishing surface. The Phase 3 proof substitutes
//         admin-queue filter-visibility for CP13/CP14's public-list
//         visibility flip; see `FISHSTANK_V2_X_SOURCE_REVIEW_AUDIT.md`
//         §5.2.
// ----------------------------------------------------------------------------

/**
 * PATCH /v2/staging/x-source-entries/:id/review-state request body.
 *
 * `to_state` must be one of `RECAP_REVIEW_STATES` (the enum is shared
 * across recap_events / tts_entries / production_messages /
 * x_source_entries — see `RecapReviewState` above). The Worker maps
 * an unknown value to `invalid_review_state`.
 *
 * `reason` is required (non-empty trimmed, ≤
 * `X_SOURCE_ENTRY_REVIEW_REASON_MAX` 1024 chars) when `to_state` is
 * `'hidden'` or `'flagged'`. It is silently dropped when `to_state`
 * is `'approved'` or `'unreviewed'`; the column is cleared to NULL
 * regardless of request input. Mirrors CP14's strict posture verbatim.
 *
 * `idempotency_key` is a client-generated UUIDv4. Same UNIQUE
 * collision semantics as CP8/CP10/CP11/CP12/CP13/CP14:
 *   - Replay with same key + same `to_state` → prior receipt verbatim.
 *   - Replay with same key + different `to_state` → 409 conflict.
 *
 * Same-state PATCH (current `review_state` already equals `to_state`)
 * surfaces `x_source_entry_review_state_unchanged` 422 — operator
 * no-op, no audit row written. The hide_reason column is NOT
 * considered in the unchanged check (identical to CP13/CP14).
 */
export interface XSourceEntryReviewStateChangeRequest {
  readonly idempotency_key: string
  readonly to_state: RecapReviewState
  readonly reason?: string | null
}

/**
 * Admin queue row. Carries the editorial-workflow columns
 * (`review_state`, `hide_reason`), the external-platform identifier
 * (`platform_id` UNIQUE), and the content/metadata columns
 * (`author_handle`, `posted_at`, `url`, `body`, `arc_id`). The admin
 * queue is unfiltered by `review_state` by default — it shows every
 * row regardless of state so an operator can flip a mistakenly-
 * approved row back to `'flagged'` without first having to re-find
 * it. `?review_state=<state>` narrows the list when the operator
 * asks.
 *
 * Deliberate omissions vs `TtsAdminQueueItem` and
 * `ProductionMessagesAdminQueueItem`:
 *   - NO `entities[]` field. The `x_source_entry_entities` link
 *     table EXISTS at the schema level (unlike CP14's
 *     production_message_entities gap), but surfacing it in CP15
 *     first slice adds a JOIN + contract field + SPA column for
 *     zero lifecycle-proof benefit. Deferred to a future additive
 *     slice. See `FISHSTANK_V2_X_SOURCE_REVIEW_AUDIT.md` X-4.
 *   - NO typed `payload` field. `x_source_entries.payload JSONB`
 *     is opaque today — no ingest producer, no consumer, no known
 *     shape. Typing it is a provenance decision out of CP15 scope.
 *     See audit X-5.
 *   - NO `voice` / `author` column. The real column is
 *     `author_handle` (nullable TEXT).
 *   - NO `occurred_at`. The real column is `posted_at`, which is
 *     NULLABLE (unlike `production_messages.occurred_at` which is
 *     NOT NULL). The contract type reflects this explicitly.
 *   - NO `arc: LiveArcRef | null`. The admin queue carries the raw
 *     `arc_id: string | null` (stringified bigint) rather than a
 *     joined arc ref, keeping the loader JOIN-free for first slice.
 *   - NO source-kind badge. `x_source_entries` has no `source` enum
 *     analogous to `tts_source`; the external platform is implicit
 *     in the `platform_id` prefix convention.
 */
export interface XSourceEntriesAdminQueueItem {
  id: string
  /**
   * External-platform UNIQUE key. Hosted-smoke seed rows use the
   * prefix `HOSTED-CP15-smoke-<epoch>-<rand>` so closeout SQL can
   * clean them up via `platform_id LIKE 'HOSTED-CP15-%'`.
   */
  platform_id: string
  author_handle: string | null
  /** NULLABLE on x_source_entries (unlike production_messages.occurred_at). */
  posted_at: string | null
  url: string | null
  body: string | null
  /** Stringified bigint FK; NULLABLE (references arcs ON DELETE SET NULL). */
  arc_id: string | null
  review_state: RecapReviewState
  hide_reason: string | null
  created_at: string
  updated_at: string
}

/**
 * Pagination block for the admin queue. Shape matches
 * `ProductionMessagesAdminQueuePagination` so renderers can share
 * the pager component.
 */
export interface XSourceEntriesAdminQueuePagination {
  page: number
  page_size: number
  total_returned: number
  has_next: boolean
  truncated: boolean
}

/**
 * Admin-queue body. Filters are echoed so the renderer can derive
 * prev/next and preserve the operator-typed query without re-parsing
 * the URL. First slice supports `?review_state=` and `?page=` only;
 * `?since=` and `?author_handle=` are deferred (see audit X-6 / §4).
 */
export interface XSourceEntriesAdminQueueBody {
  filters: {
    /** null = any state. First-slice default. */
    review_state: RecapReviewState | null
    page: number
  }
  items: XSourceEntriesAdminQueueItem[]
  pagination: XSourceEntriesAdminQueuePagination
}

export type XSourceEntriesAdminQueueArtifact =
  Envelope<XSourceEntriesAdminQueueBody>

/**
 * Audit body variant written to `mutation_events.new_row_json` when
 * an x-source-entry review-state PATCH succeeds. Discriminated by
 * `kind`. Carries the pre/post review_state pair, the pre/post
 * hide_reason pair, the operator-supplied reason, the actor cookie
 * hash, AND the row's `platform_id` — the platform id is carried
 * as a top-level audit field (CP15-specific; CP14 had no analogous
 * column) so operators searching `mutation_events` by platform
 * prefix (e.g. `HOSTED-CP15-%`) do not need a JOIN back to
 * `x_source_entries`.
 *
 * `target_table` is pinned to the `x_source_entries` literal.
 */
export interface XSourceEntryReviewStateChangeAuditBody {
  readonly kind: 'x_source_entry_review_state_change'
  readonly target_table: 'x_source_entries'
  readonly target_id: string // stringified bigint
  readonly x_source_entry_id: string // same value as target_id, namespaced for cross-variant readability
  readonly platform_id: string // carried for grep-ability in audit logs
  readonly from_state: RecapReviewState
  readonly to_state: RecapReviewState
  readonly from_hide_reason: string | null
  readonly to_hide_reason: string | null
  readonly reason: string | null
  readonly actor_cookie_hash: string
  readonly mutation_event_id: string
  readonly idempotency_key: string
}

/**
 * Receipt body returned on a successful x-source-entry review-state
 * flip. Same provenance envelope wrapping as
 * CP8/CP10/CP11/CP12/CP13/CP14. `row` carries the post-update admin
 * row snapshot so the admin SPA sees the new `review_state` AND
 * `hide_reason` immediately on flip. `prior_row` carries the pre-flip
 * snapshot so the SPA can render a compact diff or undo affordance
 * without re-fetching.
 *
 * `audit_mirror_deferred` follows the CP8/CP10/CP11/CP12/CP13/CP14
 * contract: only present (and only `true`) when the DB transaction
 * committed but the R2 audit-artifact write failed.
 */
export interface XSourceEntryReviewStateChangeReceiptBody {
  readonly row: XSourceEntriesAdminQueueItem
  readonly prior_row: XSourceEntriesAdminQueueItem
  readonly mutation_event_id: string
  readonly from_state: RecapReviewState
  readonly to_state: RecapReviewState
  readonly from_hide_reason: string | null
  readonly to_hide_reason: string | null
  readonly reason: string | null
  audit_mirror_deferred?: boolean
}

export type XSourceEntryReviewStateChangeReceipt =
  Envelope<XSourceEntryReviewStateChangeReceiptBody>

/**
 * Validation-error variants specific to the x-source-entry
 * review-state route. Mirrors CP14's
 * `ProductionMessageReviewStateValidationError` shape one-for-one
 * with discriminators namespaced to `x_source_entry_*` so the admin
 * SPA renderer can pattern-match without inspecting the route.
 *
 * Strict HTTP-status posture INHERITED FROM CP14: all validation
 * codes map to HTTP 400 EXCEPT `x_source_entry_review_state_unchanged`,
 * which maps to HTTP 422. Conflict → 409. Not-found → 404.
 */
export type XSourceEntryReviewStateValidationError =
  | { code: 'invalid_review_state'; field: 'to_state'; received: string }
  | { code: 'x_source_entry_review_state_unchanged'; field: 'to_state'; current: RecapReviewState }
  | { code: 'x_source_entry_idempotency_conflict'; field: 'idempotency_key'; prior_to_state: RecapReviewState; requested_to_state: RecapReviewState }
  | { code: 'invalid_enum'; field: string; allowed: string[] }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'x_source_entry_not_found' }
  | { code: 'x_source_entry_hide_reason_required'; field: 'reason' }
  | { code: 'x_source_entry_hide_reason_too_long'; field: 'reason'; max: number }

// ---------------------------------------------------------------------------
// CP16 — mutation_events admin queue (read-only audit/history surface)
// ---------------------------------------------------------------------------
//
// First-slice posture (see FISHSTANK_V2_SLICE2_CHECKPOINT_16_PLAN.md and
// FISHSTANK_V2_CHECKPOINT_16_FIRST_SLICE.md for the grounded scope):
//
//   - READ-ONLY. No mutation route, no review-state change, no receipt,
//     no audit-body variant. This surface exposes the existing
//     `mutation_events` table (CP8 shape, migration 0007) as an operator-
//     facing audit-history queue so operators can see WHO (by 8-hex prefix
//     of `actor_cookie_hash`, never the full hash), WHAT (target_table +
//     target_id), WHEN (created_at), and WHETHER-IT-MIRRORED-TO-R2
//     (audit_mirror_deferred, derived from artifact_key IS NULL).
//   - Allowlist-typed filters. `target_table` and `action` are both drawn
//     from closed allowlists (`MUTATION_EVENTS_TARGET_TABLES` /
//     `MUTATION_EVENTS_ACTIONS`). Off-allowlist values are rejected with
//     a typed 400 (intentional tightening over CP15's silent-fallback
//     parser — justified by the table's 5-element allowlist where a
//     silent null would show ALL tables instead of the operator's asked-
//     for table).
//   - No PII leak. The loader SQL projects `LEFT(actor_cookie_hash, 8)`
//     so the full SHA-256 hash never leaves the DB. The contract's
//     `actor_cookie_hash` field is exactly 8 hex chars at runtime.
//   - No DDL. CP16 is zero-migration; consumes CP8's existing schema.
//
// Deferred to later CP16 phases (explicitly NOT in first slice):
//   - cross-row diff view, export/download, row-granular action filters
//     beyond 'create' | 'update', `?since=` / `?actor_prefix=` filters.
//

/**
 * Closed allowlist of `mutation_events.target_table` values that the
 * CP16 admin queue accepts as a filter. These are the five editorial-
 * lifecycle tables with mutation surfaces as of CP15 closeout:
 *
 *   - `recap_events` (CP10 / CP11)
 *   - `wiki_pages` (CP10)
 *   - `tts_entries` (CP13)
 *   - `production_messages` (CP14)
 *   - `x_source_entries` (CP15)
 *
 * Kept in sync with the union of `target_table` literals used by
 * existing audit body variants (search this file for `target_table:`).
 * If a future CP adds a new mutation surface (e.g. `arcs`), append its
 * table name here AND extend the union `MutationEventsTargetTable`
 * below.
 *
 * NOT a DB-level CHECK constraint — `mutation_events.target_table` is
 * a plain TEXT column per migration 0007, so this allowlist is enforced
 * only at the admin-queue READ boundary (filter validation). Historical
 * rows with off-allowlist values remain legible via the default-no-
 * filter query.
 */
export const MUTATION_EVENTS_TARGET_TABLES = [
  'recap_events',
  'wiki_pages',
  'tts_entries',
  'production_messages',
  'x_source_entries',
  'entities',
  // Refinement Phase A2 — arc create writes target_table='arcs'.
  // The CP16 audit-history page reads this allowlist so the new rows
  // surface automatically once they start appearing in the table.
  'arcs',
  // Refinement Phase B2 — arc-participation create writes
  // target_table='arc_participations'. Same posture: the audit-
  // history page absorbs the new rows once they start flowing.
  'arc_participations',
  // Refinement Phase D — entity-alias create/update/delete writes
  // target_table='entity_aliases'. Same audit-history absorption.
  'entity_aliases',
] as const
export type MutationEventsTargetTable =
  (typeof MUTATION_EVENTS_TARGET_TABLES)[number]

/**
 * Closed allowlist of `mutation_events.action` values. CP8 committed
 * the two-value posture: 'create' | 'update'. A hypothetical 'delete'
 * would require a separate CP to decide on the soft-vs-hard-delete
 * audit shape, so it is intentionally absent.
 */
export const MUTATION_EVENTS_ACTIONS = ['create', 'update', 'delete'] as const
export type MutationEventsAction = (typeof MUTATION_EVENTS_ACTIONS)[number]

/**
 * One row in the CP16 admin audit-history queue. Mirrors the columns
 * projected by `lib/mutation-events-loader.ts`.
 *
 * Columns omitted by design (available in the DB but NOT surfaced in
 * first slice to keep the payload small and the renderer simple):
 *   - `new_row_json` / `prior_row_json` — full snapshots. First slice
 *     shows "there was a change"; a later CP16 phase may add a row-
 *     detail view that expands these.
 *   - `idempotency_key` — operator-irrelevant for history browsing.
 *   - `artifact_key` — presence is surfaced indirectly via
 *     `audit_mirror_deferred` (true = NULL artifact_key).
 */
export interface MutationEventsAdminQueueItem {
  /** `mutation_events.id` — UUID v4 string. */
  id: string
  /** ISO-8601 UTC timestamp. Rendered in the SAME shape as CP15 items. */
  created_at: string
  /**
   * Constrained to `MutationEventsTargetTable` at the filter boundary;
   * surfaced as plain string here because historical rows MAY carry
   * off-allowlist values (CP8 did not enforce a CHECK). The renderer
   * should compare against `MUTATION_EVENTS_TARGET_TABLES` and render
   * off-allowlist values as an explicit "legacy" badge rather than
   * assume membership.
   */
  target_table: string
  /** Stringified bigint or UUID — shape varies per target_table. */
  target_id: string
  /** Constrained to `MutationEventsAction` for allowlisted rows; see target_table note. */
  action: string
  /**
   * First 8 hex chars of `SHA-256(admin cookie value)`. Full 64-char
   * hash is NEVER included in the payload — see the loader's
   * `LEFT(actor_cookie_hash, 8)` projection. Non-null per migration
   * 0007 (`actor_cookie_hash TEXT NOT NULL`).
   */
  actor_cookie_hash_prefix: string
  /**
   * Derived from `artifact_key IS NULL` at read time. `true` means the
   * DB row committed but the R2 audit-artifact write failed (or was
   * deferred) — the operator should investigate the mirror pipeline,
   * not the mutation itself.
   */
  audit_mirror_deferred: boolean
}

/**
 * Pagination block for the admin audit-history queue. Shape MATCHES
 * `XSourceEntriesAdminQueuePagination` / `ProductionMessagesAdminQueuePagination`
 * so the admin SPA reuses the same pager component.
 */
export interface MutationEventsAdminQueuePagination {
  page: number
  page_size: number
  total_returned: number
  has_next: boolean
  truncated: boolean
}

/**
 * Admin-queue body. Filters are echoed back (normalised to the closed
 * allowlists) so the renderer can derive prev/next links and restore
 * the operator-typed filter state without re-parsing the URL.
 *
 * First slice supports exactly four filter axes. All are optional;
 * all default to `null` (= no filter on that axis). Off-allowlist
 * values are REJECTED with a typed 400 at the parser boundary — they
 * never reach this body.
 */
export interface MutationEventsAdminQueueBody {
  filters: {
    /** null = any table. */
    target_table: MutationEventsTargetTable | null
    /** null = any row. Free-text match on `mutation_events.target_id`. */
    target_id: string | null
    /** null = any action. */
    action: MutationEventsAction | null
    page: number
  }
  items: MutationEventsAdminQueueItem[]
  pagination: MutationEventsAdminQueuePagination
}

export type MutationEventsAdminQueueArtifact =
  Envelope<MutationEventsAdminQueueBody>

/**
 * Validation-error variants for the CP16 admin audit-history queue.
 * All four map to HTTP 400.
 *
 * Intentional tightening over CP15 (which silently falls back to null
 * on bad filter input): this surface uses closed allowlists on two
 * axes, and a silent-null-fallback would show ALL tables when the
 * operator meant to narrow to one — a safety regression, not a
 * defensive win. The parser therefore throws a typed validation
 * error, the handler maps it to HTTP 400, and the SPA renders the
 * specific reason.
 */
export type MutationEventsAdminQueueValidationError =
  | {
      code: 'mutation_events_invalid_target_table'
      field: 'target_table'
      received: string
      allowed: readonly string[]
    }
  | {
      code: 'mutation_events_invalid_target_id'
      field: 'target_id'
      received: string
      /** Human-readable hint; no regex leaked to operator. */
      reason: 'empty_string' | 'too_long'
      max?: number
    }
  | {
      code: 'mutation_events_invalid_action'
      field: 'action'
      received: string
      allowed: readonly string[]
    }
  | {
      code: 'mutation_events_invalid_page'
      field: 'page'
      received: string
      reason: 'not_a_number' | 'not_positive' | 'exceeds_max_pages'
      max_pages?: number
    }

// -- Entity edit (CP17) --------------------------------------------------
//
// Slice 2 Checkpoint 17 scope:
//   - First write-side contract block for `entities`. CP1 surfaced the
//     read shape; CP17 adds the narrow edit surface for the four core
//     columns operators most commonly need to fix:
//
//       PATCH /v2/staging/entities/:id          → EntityMutationReceipt
//       GET   /v2/staging/entities/admin/:id.json → EntityForAdminArtifact
//
//   - Admin-only enum mirrors. The DB enum strings are
//     `('fish','freeloader','host','production','other')` for
//     entity_kind (migration 0011 superseded 0010's 'robot' with
//     'other') and `('active','merged','retired','draft')` for
//     entity_status. The public `EntityKind` type was realigned to
//     the same DB taxonomy as part of the 0010 cutover and tracks
//     0011's robot→other swap, so the admin mirror and the public
//     type share values; the mirror is kept under its own name so
//     the admin surface can evolve independently if needed.
//
//   - `status='merged'` is rejected at the parser. The merge flow is
//     a separate future checkpoint and the row's merge state is
//     governed by `merged_into_entity_id` + the
//     `entities_prevent_merge_chain` trigger, not by direct flips of
//     `status`.
//
//   - One R2 audit artifact per mutation:
//       ${R2_PREFIX_V2_STAGING}mutations/entities/${id}/${idempotency_key}.json
//       body = EntityAuditArtifact. DB-first / R2-second; on R2
//       failure the receipt's `audit_mirror_deferred` flag flips and
//       `mutation_events.artifact_key` stays NULL as the drift detector
//       (same posture as every CP8+ admin write).

/**
 * Entity-kind enum exactly as written by the DB. Used by the admin
 * edit form's `<select>` and by the loader's parser allowlist.
 *
 * Identical in values to the public `EntityKind` (post-0010
 * realignment, post-0011 robot→other swap). Kept under its own
 * name so the admin surface can evolve independently if the public
 * surface ever diverges again.
 */
export type EntityKindAdmin =
  | 'fish'
  | 'freeloader'
  | 'host'
  | 'production'
  | 'other'

/**
 * Runtime allowlist mirroring the `EntityKindAdmin` type. Used by
 * parsers (e.g. the arc_participations create/edit kind_override
 * validator from migration 0013) and by SPA selects that need to
 * iterate the legal values. Order is the canonical product order
 * (fish → freeloader → host → production → other), matching how
 * the admin Entities table sorts categorical filters.
 */
export const ENTITY_KINDS_ADMIN = [
  'fish',
  'freeloader',
  'host',
  'production',
  'other',
] as const satisfies readonly EntityKindAdmin[]

/**
 * Entity-status enum exactly as written by the DB. Includes 'merged'
 * because the column itself can hold that value (set by the future
 * merge flow); CP17's edit parser REJECTS 'merged' as a target value
 * but the type is wide so a hydrated row from a soft-merged row
 * round-trips correctly through the receipt.
 */
export type EntityStatusAdmin =
  | 'active'
  | 'merged'
  | 'retired'
  | 'draft'

/**
 * Closed allowlist of statuses the CP17 edit parser will accept.
 * 'merged' is intentionally absent.
 */
export const ENTITY_STATUSES_EDITABLE_ADMIN = [
  'active',
  'retired',
  'draft',
] as const
export type EntityStatusEditable =
  (typeof ENTITY_STATUSES_EDITABLE_ADMIN)[number]

/**
 * Admin-facing row shape for `entities`. Wider than the public
 * `Entity` (which hides `id`, `merged_into_entity_id`, media-asset
 * ids, `short_bio`, timestamps) because the edit form needs the full
 * picture to render and to decide whether the form is read-only
 * (soft-merged rows render with a banner pointing to the future
 * merge flow).
 *
 * Stringified bigint ids — JSON precision safety.
 *
 * `kind` and `status` use the admin-only enum mirrors above.
 */
export interface EntityForAdmin {
  /** `entities.id` — stringified bigint. */
  id: string
  slug: string
  primary_name: string
  kind: EntityKindAdmin
  status: EntityStatusAdmin
  /** Stringified bigint or null. Non-null = soft-merged row. */
  merged_into_entity_id: string | null
  /** Stringified bigint or null. CP17 surfaces but does not edit. */
  avatar_asset_id: string | null
  /** Stringified bigint or null. CP17 surfaces but does not edit. */
  hero_asset_id: string | null
  /** CP17 surfaces but does not edit. */
  short_bio: string | null
  created_at: string // ISO-8601
  updated_at: string // ISO-8601
}

/**
 * Snapshot of an entity row at audit time. Stored verbatim in
 * `mutation_events.{prior,new}_row_json`. Same shape as
 * `EntityForAdmin` — the audit body just renames the field for
 * symmetry with the other CP-N audit artifacts.
 */
export type EntityRowSnapshot = EntityForAdmin

/**
 * PATCH body for `/v2/staging/entities/:id`. Whitelist-strict: the
 * loader parser only recognises these five fields. Unknown fields
 * are silently ignored. At least one editable field must be present
 * (i.e., a body containing only `idempotency_key` is rejected with
 * `no_editable_fields`).
 */
export interface EntityEditRequest {
  primary_name?: string
  slug?: string
  kind?: EntityKindAdmin
  /**
   * Editable values are the closed allowlist
   * (`'active' | 'retired' | 'draft'`). Sending `'merged'` here is a
   * parser error — the merge flow is the only path that flips the
   * column to that value.
   */
  status?: EntityStatusEditable
  /**
   * Stringified `media_assets.id` (digit-string) to attach a portrait,
   * or `null` to clear the current portrait. Added in Slice 1 of the
   * entity-portraits build. Operator's two-step flow: upload via
   * `POST /v2/staging/entities/:id/portrait` → receive
   * `EntityPortraitUploadReceipt.media_asset_id` → PATCH the entity
   * here with that id. `null` is the explicit clear.
   */
  avatar_asset_id?: string | null
  /** Required UUID v4. Client (admin SPA) generates per form open. */
  idempotency_key: string
}

/**
 * POST body for `/v2/staging/entities`. Whitelist-strict: the loader
 * parser only recognises these six fields. Unknown fields are silently
 * ignored.
 *
 * Required: `slug`, `kind`, `primary_name`, `idempotency_key`.
 * Optional: `status` (defaults to `'active'`), `avatar_asset_id`
 * (defaults to absent → NULL in DB).
 *
 * Created in the entity-create slice (Slice A of the new-entity
 * close-out, 2026-05-05). Mirrors the CP17 edit-side parser shape
 * but with a smaller field set and tighter required-vs-optional
 * posture: create cannot be a no-op so there's no `no_editable_fields`
 * equivalent, and the merge-status rule is the same as on edit.
 */
export interface EntityCreateRequest {
  slug: string
  kind: EntityKindAdmin
  primary_name: string
  /**
   * Editable values are the closed allowlist
   * (`'active' | 'retired' | 'draft'`). Sending `'merged'` is a parser
   * error — same posture as the CP17 edit. Defaults to `'active'`
   * when absent.
   */
  status?: EntityStatusEditable
  /** Optional. Same digit-string-or-null shape as CP17's edit parser. */
  avatar_asset_id?: string | null
  /** Required UUID v4. */
  idempotency_key: string
}

/**
 * Validation-error variants for the entity edit AND create surfaces.
 * All map to HTTP 400 except `csrf_failed` (403, surfaced by the worker
 * shell, not the loader) and `entity_not_found` (404, surfaced by
 * the worker shell when the loader throws `EntityNotFound`).
 *
 * Create-specific variants (`slug_required`, `kind_required`,
 * `primary_name_required` against the create parser) reuse the existing
 * `*_required` shapes where compatible; new variants added below the
 * edit-specific ones.
 */
export type EntityValidationError =
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'invalid_enum'; field: string; allowed: readonly string[] }
  | { code: 'no_editable_fields' }
  | { code: 'primary_name_required'; field: 'primary_name' }
  | { code: 'primary_name_too_long'; field: 'primary_name'; max: number }
  | { code: 'invalid_slug'; field: 'slug' }
  | { code: 'field_too_long'; field: string; max: number }
  | { code: 'field_too_short'; field: string; min: number }
  | { code: 'slug_conflict'; field: 'slug' }
  | { code: 'merged_status_not_editable'; field: 'status' }
  | { code: 'idempotency_key_in_use'; field: 'idempotency_key' }
  // -- create-only variants --
  | { code: 'slug_required'; field: 'slug' }
  | { code: 'kind_required'; field: 'kind' }
  | { code: 'invalid_body' }

/**
 * Receipt body returned by the loader and wrapped in a `Provenance`
 * envelope by the worker. Identical shape to CP8/CP10 receipts so
 * the admin SPA reuses the same renderer.
 *
 * `audit_mirror_deferred` is `true` only when the post-commit R2
 * mirror failed — the DB row is the authority either way.
 */
export interface EntityMutationReceiptBody {
  row: EntityForAdmin
  /** Null only on a future create flow; CP17 is edit-only so always populated. */
  prior_row: EntityForAdmin | null
  /** `mutation_events.id` — stringified bigint. */
  mutation_event_id: string
  audit_mirror_deferred?: true
}

export type EntityMutationReceipt = Envelope<EntityMutationReceiptBody>

/**
 * Body of the admin single-entity GET. The worker writes the
 * envelope and an `artifact_writes` row per call (same provenance
 * discipline as every other admin read endpoint).
 */
export interface EntityForAdminBody {
  entity: EntityForAdmin
}

export type EntityForAdminArtifact = Envelope<EntityForAdminBody>

/**
 * Body of the R2 audit artifact written post-commit. Mirrors the
 * CP8 `RecapEventAuditArtifact` shape exactly so the audit-history
 * renderer can treat all mutation kinds uniformly.
 *
 * `action` widened to `'update' | 'create'` in the entity-create slice
 * (2026-05-05). On `'create'`, `prior_row` is `null`; on `'update'`,
 * `prior_row` is the pre-edit snapshot. The audit-history renderer
 * already handles both shapes uniformly via the existing recap-event
 * audit precedent.
 */
export interface EntityAuditArtifactBody {
  target_table: 'entities'
  target_id: string
  action: 'update' | 'create'
  prior_row: EntityForAdmin | null
  new_row: EntityForAdmin
  mutation_event_id: string
  actor_cookie_hash: string
  idempotency_key: string
}

export type EntityAuditArtifact = Envelope<EntityAuditArtifactBody>

/**
 * One row in the admin entity-list (CP17 Phase 2). Wider than the
 * public `Entity` envelope only by `id`, `merged_into_entity_id`, and
 * `updated_at` — the SPA needs `id` to render the per-row Edit link
 * (the public envelope intentionally omits ids), `merged_into_entity_id`
 * to gray-out soft-merged rows in the Edit affordance, and
 * `updated_at` to sort by recency.
 *
 * Aliases / social links / avatar / hero / portrait / arc participation
 * are deliberately omitted — the admin list view is dense and
 * rendering those would crowd the table. They remain accessible via
 * the single-entity admin GET when needed.
 */
export interface EntitiesAdminListItem {
  id: string
  slug: string
  primary_name: string
  kind: EntityKindAdmin
  status: EntityStatusAdmin
  merged_into_entity_id: string | null
  updated_at: string // ISO-8601
}

export interface EntitiesAdminListBody {
  entities: EntitiesAdminListItem[]
  /**
   * True when the row count was capped by the loader's hard limit.
   * The first slice does not paginate; if the operator hits the cap
   * the SPA renders a truncation banner pointing at the future
   * search affordance.
   */
  truncated: boolean
}

export type EntitiesAdminListArtifact = Envelope<EntitiesAdminListBody>

// -- Arcs admin (Refinement Phase A1) ------------------------------------
//
// First slice of the post-CP17 arcs / entity-linkage / category /
// aliases refinement. Replaces the `Arcs (soon)` admin shell
// placeholder with a real list-only surface. No mutation contract is
// added here — arc create / arc full-row edit / arc-summary edit
// (already shipped under CP10) are out of A1 scope.
//
//   GET /v2/staging/arcs/admin/list.json → ArcAdminListArtifact
//
// The single-arc admin GET, the create POST, and the full-row PATCH
// land in subsequent phases per
// FISHSTANK_V2_ARCS_ENTITIES_REFINEMENT_PLAN.md §2 Phase A2/A3.

/**
 * Mirror of the DB enum `arc_type` after migration 0008. The
 * `'spinoff'` value is the addition; the existing values are
 * preserved verbatim so historical rows round-trip through the
 * read path. The admin create + edit forms restrict their
 * `<select>` to the closed allowlist `ARC_TYPES_EDITABLE` below;
 * `'off_season'` is intentionally absent from that allowlist.
 */
export type ArcType = 'season' | 'off_season' | 'special' | 'spinoff'

/**
 * Closed allowlist of arc_type values offered by the admin
 * create + edit forms. `'off_season'` is deliberately absent —
 * historical rows that already carry it round-trip through the
 * read path, but the admin write surface does not propagate it
 * to new rows. (Keeps the admin form aligned with the user's
 * product direction: seasons / specials / spinoffs.)
 */
export const ARC_TYPES_EDITABLE = ['season', 'special', 'spinoff'] as const
export type ArcTypeEditable = (typeof ARC_TYPES_EDITABLE)[number]

/**
 * One row on the admin Arcs list backing the new top-level
 * `/arcs` admin page. Wider than the CP10 `WikiAdminArcRow` shape
 * (which is summary-edit-focused): adds `arc_type`,
 * `display_order`, `start_date`, `end_date`, `winner_entity_id`,
 * and the two count fields the operator uses to gauge how
 * populated each arc is at a glance.
 *
 * Stringified bigint ids — JSON precision safety, same convention
 * the entities loader uses.
 */
export interface ArcAdminListItem {
  /** `arcs.id` — stringified bigint. */
  id: string
  slug: string
  title: string
  /** `arcs.status` — full closed allowlist (mirror of `arc_status`). */
  status: 'scheduled' | 'active' | 'completed' | 'cancelled'
  arc_type: ArcType
  display_order: number | null
  /** ISO date (yyyy-mm-dd) or null. */
  start_date: string | null
  /** ISO date (yyyy-mm-dd) or null. */
  end_date: string | null
  /**
   * First 240 characters of `summary_md`, null when the column
   * is null. Renderer never shows full markdown on the list page;
   * the existing CP10 summary-edit surface owns the full editor.
   */
  summary_md_preview: string | null
  /** Stringified bigint or null. CP10's per-arc page hydrates the entity ref. */
  winner_entity_id: string | null
  /** count(arc_participations) for this arc — non-negative integer. */
  participation_count: number
  /** count(challenges) for this arc — non-negative integer. */
  challenge_count: number
  /** ISO-8601 from `arcs.updated_at`. Never null in practice (trigger). */
  updated_at: string | null
}

/**
 * Body for the admin Arcs list. `truncated` matches the
 * convention used by CP10 wiki admin / CP17 entities admin —
 * true iff the loader's hard cap was hit. The first slice does
 * NOT paginate; if the operator hits the cap the SPA renders a
 * truncation banner pointing at a future search affordance.
 */
export interface ArcAdminListBody {
  arcs: readonly ArcAdminListItem[]
  truncated: boolean
}

export type ArcAdminListArtifact = Envelope<ArcAdminListBody>

// -- Arcs admin write (Refinement Phase A2) ------------------------------
//
// First mutation surface for `arcs`. Mirrors the CP8 recap-event-create
// shape narrowed to one table and five required fields:
//
//   POST /v2/staging/arcs                          → ArcMutationReceipt
//
// Whitelist-strict body shape: only the five fields below are read by
// the parser. Unknown fields are silently ignored (matches every
// other CP8+ create surface).
//
// Receipt envelope mirrors the seven existing CP8+ surfaces. `prior_row`
// is null on create.
//
// One R2 audit artifact per mutation:
//   ${R2_PREFIX_V2_STAGING}mutations/arcs/${arc_id}/${idempotency_key}.json
//   body = ArcAuditArtifact. DB-first / R2-second; on R2 failure the
//   receipt's `audit_mirror_deferred` flag flips and
//   `mutation_events.artifact_key` stays NULL as the drift detector
//   (same posture as every CP8+ admin write).
//
// CSRF action label: `arc-write` (so A3's edit/delete reuse it).
//
// Arc-edit (PATCH) and arc-delete are explicitly NOT in A2 — they land
// in subsequent refinement phases per
// FISHSTANK_V2_ARC_CREATE_PLAN.md "A2 slice boundary".

// `ArcStatus` (the wide enum mirror, all four values) is already
// defined above at line ~209 (CP3-era). `ArcRowSnapshot` reuses it
// so a row that ends up `'cancelled'` round-trips cleanly through
// the receipt. The admin create form's `<select>` is restricted to
// `ARC_STATUSES_EDITABLE` below — `'cancelled'` is intentionally
// absent (terminal admin-only edge case deferred to A3).

/**
 * Closed allowlist of `arc_status` values offered by the A2 create
 * form. `'cancelled'` is intentionally absent.
 */
export const ARC_STATUSES_EDITABLE = [
  'scheduled',
  'active',
  'completed',
] as const
export type ArcStatusEditable = (typeof ARC_STATUSES_EDITABLE)[number]

/**
 * Full DB-shape mirror of one row in `arcs`. Stringified bigint ids
 * (`id`, `winner_entity_id`) for JSON precision. Carried verbatim in
 * the receipt's `row` and `prior_row` fields, and in
 * `mutation_events.new_row_json` (and `prior_row_json` once A3's edit
 * lands).
 *
 * `status` is the **wide** enum (4 values) — see note above on
 * `ArcStatus`. `arc_type` is the wide enum (4 values) — historical
 * rows may carry `'off_season'` even though the create form rejects it.
 */
export interface ArcRowSnapshot {
  /** `arcs.id` — stringified bigint. */
  id: string
  slug: string
  title: string
  status: ArcStatus
  arc_type: ArcType
  display_order: number | null
  /** ISO date (yyyy-mm-dd) or null. */
  start_date: string | null
  /** ISO date (yyyy-mm-dd) or null. */
  end_date: string | null
  summary_md: string | null
  /** stringified bigint or null. */
  winner_entity_id: string | null
  /** ISO-8601. */
  created_at: string
  /** ISO-8601. */
  updated_at: string
}

/**
 * Whitelist-strict request body for POST /v2/staging/arcs. Five
 * fields, all required.
 */
export interface ArcCreateRequest {
  slug: string
  title: string
  arc_type: ArcTypeEditable
  status: ArcStatusEditable
  /** uuid v4 — caller generates one per form open and reuses across retries. */
  idempotency_key: string
}

/**
 * Closed validation-error union for A2 (parser-side errors + remapped
 * DB constraints). The discriminated `code` lets the SPA render
 * inline under the offending field.
 *
 * `another_arc_active` is the typed remap of the partial-unique
 * `arcs_one_active` constraint — without this the operator would see
 * a generic 500 when trying to create a second active arc.
 */
export type ArcValidationError =
  | { code: 'invalid_body' }
  | { code: 'slug_required'; field: 'slug' }
  | { code: 'slug_too_long'; field: 'slug'; max: number }
  | { code: 'invalid_slug'; field: 'slug' }
  | { code: 'title_required'; field: 'title' }
  | { code: 'title_too_long'; field: 'title'; max: number }
  | { code: 'invalid_enum'; field: string; allowed: readonly string[] }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'slug_conflict'; field: 'slug' }
  | { code: 'another_arc_active'; field: 'status' }
  | { code: 'idempotency_key_in_use'; field: 'idempotency_key' }
  // Refinement Phase A3 — additive variants for the edit surface.
  // `no_editable_fields` mirrors CP17's empty-delta guard.
  // `arc_not_found` is the typed 404 for PATCH / GET when the id
  // doesn't resolve.
  // `invalid_date` covers start_date / end_date parse failures
  // (yyyy-mm-dd is the only accepted format).
  // `invalid_integer` covers display_order shape failures.
  // `end_before_start` is both a parser pre-check (against the
  // merged shape: delta + prior row's untouched fields) AND the
  // typed remap of the DB CHECK `arcs_end_after_start` (code 23514).
  | { code: 'no_editable_fields' }
  | { code: 'arc_not_found' }
  | { code: 'invalid_date'; field: 'start_date' | 'end_date' }
  | { code: 'invalid_integer'; field: 'display_order' }
  | { code: 'end_before_start'; field: 'end_date' }

/**
 * Receipt body returned on a successful create. Same envelope shape
 * as the seven existing CP8+ mutation surfaces. `prior_row` is always
 * `null` on create. `audit_mirror_deferred` is `true` only when the
 * post-commit R2 mirror failed — the DB row is the authority either
 * way.
 */
export interface ArcMutationReceiptBody {
  row: ArcRowSnapshot
  /** Always null on create; surfaced for shape-symmetry with edit/delete. */
  prior_row: ArcRowSnapshot | null
  /** `mutation_events.id` — stringified bigint. */
  mutation_event_id: string
  audit_mirror_deferred?: true
}

export type ArcMutationReceipt = Envelope<ArcMutationReceiptBody>

/**
 * R2 audit-artifact body, mirrors `EntityAuditArtifactBody` and
 * `RecapEventAuditArtifactBody`.
 */
export interface ArcAuditArtifactBody {
  target_table: 'arcs'
  /** stringified bigint */
  target_id: string
  /**
   * Refinement Phase A3 — widened from `'create'` to
   * `'create' | 'update'`. Backwards-compatible with A2's audit
   * blobs already on R2 (their `'create'` literal is valid under
   * the wider type).
   */
  action: 'create' | 'update'
  /**
   * Null on create; full prior `ArcRowSnapshot` on update.
   * Refinement Phase A3 widened this from `null` to
   * `ArcRowSnapshot | null` (additive — old blobs still validate).
   */
  prior_row: ArcRowSnapshot | null
  new_row: ArcRowSnapshot
  /** stringified bigint */
  mutation_event_id: string
  actor_cookie_hash: string
  idempotency_key: string
}

export type ArcAuditArtifact = Envelope<ArcAuditArtifactBody>

// -- Single-arc admin GET (Refinement Phase A3) --------------------------
//
// Backs the new `GET /v2/staging/arcs/admin/:id.json` route. The admin
// SPA's edit page (`apps/admin/src/pages/ArcEdit.tsx`) hits this on
// mount to seed local form state with the row's current values.
// Mirrors CP17's `EntityForAdminBody` / `EntityForAdminArtifact`.

export interface ArcForAdminBody {
  arc: ArcRowSnapshot
}

export type ArcForAdminArtifact = Envelope<ArcForAdminBody>

// -- Edit request (Refinement Phase A3) ----------------------------------
//
// Whitelist-strict PATCH body for `PATCH /v2/staging/arcs/:id`.
// All seven editable fields are optional; the parser rejects an empty
// delta with `no_editable_fields`. `winner_entity_id` is intentionally
// absent — entity ↔ arc linkage lives in Phase B.
//
// Date fields accept ISO yyyy-mm-dd OR null. Sending `null` clears the
// column; omitting the field leaves the prior value. The parser pre-
// checks `start_date <= end_date` against the merged shape (delta +
// prior row's untouched fields) and emits `end_before_start` when
// violated. The DB CHECK (`arcs_end_after_start`, code 23514) is the
// belt-and-suspenders; the loader remaps it to the same typed error.
//
// CSRF action label is reused from A2: `arc-write`.

export interface ArcEditRequest {
  slug?: string
  title?: string
  arc_type?: ArcTypeEditable
  status?: ArcStatusEditable
  display_order?: number | null
  /** ISO yyyy-mm-dd or null. */
  start_date?: string | null
  /** ISO yyyy-mm-dd or null. */
  end_date?: string | null
  /** uuid v4 — caller generates one per form open. */
  idempotency_key: string
}


// -- Arc participations admin (Refinement Phase B1) ---------------------
//
// First admin surface for `arc_participations`. Phase B1 is read-only;
// mutation surfaces (POST/PATCH/DELETE) land in B2/B3.
//
//   GET /v2/staging/arcs/admin/:id/participations.json
//     → ArcParticipationsAdminListArtifact
//
// Admin-only mirror enums: the public `ParticipationRole` and
// `ParticipationStatus` types in this file (lines ~95–113) are
// drifted from the DB enum. They are NOT touched in B1. Phase B
// introduces admin-only mirrors that match the DB exactly,
// following CP17's `EntityKindAdmin` / `EntityStatusAdmin`
// precedent.
//
// CSRF is not relevant — B1 is a GET. B2/B3 will use the
// `arc-participation-write` action label (a new label, distinct
// from `arc-write` so the participations surface can evolve
// independently of the arc-row surface).

/**
 * Mirror of the DB enum `participation_role` (migration 0001).
 * The DB carries: 'host', 'competitor', 'guest', 'recurring'.
 *
 * Drifts from the public `ParticipationRole` (declared earlier in
 * this file) which is inaccurate — the public type carries
 * 'commentator', 'staff', 'other' that don't exist in the DB. The
 * public type is not touched here; this admin mirror is what
 * B2/B3 forms key off of.
 */
export type ParticipationRoleAdmin =
  | 'host'
  | 'competitor'
  | 'guest'
  | 'recurring'

/**
 * Closed allowlist of `participation_role` values offered by the
 * admin form `<select>`s in B2/B3. All four values are
 * operationally meaningful; nothing is reserved.
 */
export const PARTICIPATION_ROLES_EDITABLE = [
  'host',
  'competitor',
  'guest',
  'recurring',
] as const
export type ParticipationRoleEditable =
  (typeof PARTICIPATION_ROLES_EDITABLE)[number]

/**
 * Mirror of the DB enum `participation_status` (migration 0001).
 * The DB carries: 'active', 'eliminated', 'quit', 'winner', 'dnf'.
 *
 * Drifts from the public `ParticipationStatus` which is inaccurate
 * — public has 'withdrawn' instead of 'quit', adds 'other', and
 * lacks 'dnf'. The public type is not touched here.
 *
 * `'winner'` is the operational truth value the B3 winner flow
 * sets (atomically with `arcs.winner_entity_id`). Read-only B1
 * surfaces it as-is.
 */
export type ParticipationStatusAdmin =
  | 'active'
  | 'eliminated'
  | 'quit'
  | 'winner'
  | 'dnf'

export const PARTICIPATION_STATUSES_EDITABLE = [
  'active',
  'eliminated',
  'quit',
  'winner',
  'dnf',
] as const
export type ParticipationStatusEditable =
  (typeof PARTICIPATION_STATUSES_EDITABLE)[number]

/**
 * One row in the per-arc admin participations list. Carries the
 * full row plus the joined entity ref the SPA needs for display
 * without a second fetch. Stringified bigint ids — JSON precision
 * safety.
 *
 * Soft-merged entities are filtered out at the loader (mirrors
 * the public `wiki/arc.json` posture). The `entity` field is
 * therefore always populated and never points at a soft-merged
 * row.
 */
export interface ArcParticipationAdminItem {
  /** `arc_participations.id` — stringified bigint. */
  id: string
  /** stringified bigint */
  arc_id: string
  /** stringified bigint */
  entity_id: string
  entity: {
    slug: string
    /** `entities.primary_name`. */
    display_name: string
    /**
     * `entities.kind` — the product taxonomy category
     * (`fish` | `freeloader` | `host` | `production` | `other`).
     *
     * Surfaced on this admin row so the per-arc participations
     * table can render a Category column projected from entity
     * kind. Replaces the old editable role <select> as of the
     * Arc participant taxonomy correction (operators now manage
     * category by editing the entity, not the participation).
     */
    kind: EntityKindAdmin
  }
  /**
   * `arc_participations.kind_override` — per-arc category override
   * (introduced in migration 0013). NULL means "no override; use
   * `entity.kind`". Same enum domain as `entity.kind`.
   *
   * Effective category for rendering = `kind_override ?? entity.kind`.
   *
   * Required because the canonical roster (Refinement Phase D
   * follow-on) shows the same entity sometimes plays different
   * categorical roles in different arcs (e.g. Burt: S3 fish, S5
   * freeloader; Sam Hyde: S1–S4 host, Bloodgames freeloader).
   */
  kind_override: EntityKindAdmin | null
  /**
   * `arc_participations.display_alias_id` — per-arc display alias FK
   * (migration 0014). Stringified bigint. NULL means "no override;
   * render `entity.display_name`". When set, points to a row in
   * `entity_aliases` whose `entity_id` MUST equal the participation's
   * entity (enforced by trigger). Surfaced on the admin item so the
   * edit form can pre-select the current value in the alias picker.
   */
  display_alias_id: string | null
  /**
   * Resolved alias text — `entity_aliases.alias` for the row pointed
   * to by `display_alias_id`, or NULL when no override is set.
   * Surfaced for the admin view-mode display and for receipt audit
   * snapshots (so audit history shows what alias was selected, not
   * a bare FK id).
   */
  display_alias_text: string | null
  /**
   * `arc_participations.role`. Retained on the contract for round-
   * trip compatibility with audit replays and existing tests.
   * The admin UI no longer surfaces this as an editable control —
   * new rows ride the DB default ('competitor'). Future cleanup
   * may drop the column once we've confirmed nothing else
   * consumes it.
   */
  role: ParticipationRoleAdmin
  status: ParticipationStatusAdmin
  /** ISO-8601 or null. */
  joined_at: string | null
  /** ISO-8601 or null. */
  left_at: string | null
  notes: string | null
  /** ISO-8601 from `arc_participations.created_at`. */
  created_at: string
  /** ISO-8601 from `arc_participations.updated_at`. */
  updated_at: string
}

/**
 * Body of `GET /v2/staging/arcs/admin/:id/participations.json`.
 *
 * `arc_id` echoes the requested id so the SPA can verify the
 * response matches the URL parameter without re-parsing the
 * route.
 *
 * Sort: winners first, then competitors, else everyone; ties
 * break by `LOWER(entity.display_name)` ASC. Mirrors the existing
 * public `wiki/arc.json` ordering so the admin and public views
 * show the same row order.
 */
export interface ArcParticipationsAdminListBody {
  arc_id: string
  participations: readonly ArcParticipationAdminItem[]
}

export type ArcParticipationsAdminListArtifact =
  Envelope<ArcParticipationsAdminListBody>

// -- Entity-side participations admin (Refinement Phase B4) -------------
//
// Entity-side mirror of the B1 read endpoint. Where B1 answers
// "given an arc, who's in it?", B4 answers "given an entity, what
// arcs is it in?". Both are read-only admin surfaces; mutations
// (POST/PATCH/DELETE on `arc_participations`) live on the arc-side
// surface (B2/B3).
//
//   GET /v2/staging/entities/admin/:id/participations.json
//     → EntityArcParticipationsAdminListArtifact
//
// Wire shape diverges from B1's `ArcParticipationAdminItem`: this
// row denorms `arc` (id + title + slug + status + display_order)
// instead of `entity` (slug + display_name), because the entity
// is the *caller* on this surface. B1's shape stays byte-identical.
//
// Filter posture (loader-side):
//   - WHERE ap.entity_id = $1::bigint
//   - NO filter on `arcs.status` — cancelled and completed arcs
//     are returned (audit doc §5).
//   - NO filter on `entities.merged_into_entity_id` — the entity
//     IS the caller; soft-merge surfacing is handled by the
//     EntityEdit page banner.
//
// Sort posture (loader-side):
//   ORDER BY a.display_order ASC NULLS LAST,
//            LOWER(a.title) ASC,
//            ap.id ASC
//
// `status='winner'` is surfaced verbatim (no sort priority); the
// SPA renders a quiet badge instead of reordering chronological
// scan.
//
// 404 contract: when the entity doesn't exist, the worker returns
// HTTP 404 with `{ok:false, error:{code:'entity_not_found'}}`.
// Distinct from CP10's `WikiValidationError.entity_not_found`
// variant (which is scoped to the wiki create surface) — the B4
// 404 is shaped as a free-standing `EntityParticipationsValidationError`
// to keep the participations-read surface independent of the
// wiki page surface.

export type EntityParticipationsValidationError =
  | { code: 'entity_not_found' }

/**
 * Refinement Phase B4 — entity-side admin row for the
 * participations read endpoint. Bisymmetric with
 * `ArcParticipationAdminItem`: where the arc-side row denorms
 * `entity` (slug + display_name), this row denorms `arc` (id +
 * title + slug + status + display_order). Both shapes are
 * read-only; B4 ships no mutation surface.
 */
export interface EntityArcParticipationAdminItem {
  /** `arc_participations.id` — stringified bigint. */
  id: string
  /** stringified bigint */
  arc_id: string
  /** stringified bigint */
  entity_id: string
  arc: {
    /** stringified bigint — duplicate of `arc_id` for parity with B1's denorm posture. */
    id: string
    /** `arcs.title`. */
    title: string
    /** `arcs.slug`. */
    slug: string
    /** `arcs.status`. */
    status: ArcStatus
    /** `arcs.display_order` — nullable. */
    display_order: number | null
  }
  /**
   * `arc_participations.kind_override` — per-arc category override
   * (introduced in migration 0013). NULL means "no override; use
   * the entity's `kind`". Same enum domain as `EntityKindAdmin`.
   *
   * On the entity-side row we don't denorm `entity.kind` (the
   * entity IS the caller, so the SPA already knows it from the
   * EntityEdit page header). Effective category for rendering =
   * `kind_override ?? <entity.kind from page header>`.
   */
  kind_override: EntityKindAdmin | null
  /**
   * `arc_participations.display_alias_id` (migration 0014). Per-arc
   * display alias FK as stringified bigint, or NULL for "use entity
   * primary_name". Same shape as `ArcParticipationsAdminListItem`.
   * Read-only on the entity-side participations view (editing is
   * surfaced on `ArcEdit`'s per-row control to keep one source of
   * truth — same posture as `kind_override`).
   */
  display_alias_id: string | null
  /** Resolved alias text. NULL when `display_alias_id` is NULL. */
  display_alias_text: string | null
  role: ParticipationRoleAdmin
  status: ParticipationStatusAdmin
  /** ISO-8601 or null. */
  joined_at: string | null
  /** ISO-8601 or null. */
  left_at: string | null
  notes: string | null
  /** ISO-8601 from `arc_participations.created_at`. */
  created_at: string
  /** ISO-8601 from `arc_participations.updated_at`. */
  updated_at: string
}

/**
 * Body of `GET /v2/staging/entities/admin/:id/participations.json`.
 *
 * `entity_id` echoes the requested id so the SPA can verify the
 * response matches the URL parameter without re-parsing the
 * route.
 */
export interface EntityArcParticipationsAdminListBody {
  entity_id: string
  participations: readonly EntityArcParticipationAdminItem[]
}

export type EntityArcParticipationsAdminListArtifact =
  Envelope<EntityArcParticipationsAdminListBody>

// -- Arc participations admin write (Refinement Phase B2) ---------------
//
// First mutation surface for `arc_participations`. POSTs add a single
// linkage row; PATCH (edit) and DELETE land in B3 with the atomic
// winner flow (`arcs.winner_entity_id` co-update).
//
//   POST /v2/staging/arc-participations
//     → ArcParticipationMutationReceipt
//
// CSRF action label: `arc-participation-write` (new label, distinct
// from A2/A3's `arc-write` so the participations surface evolves
// independently of the arc-row surface).
//
// Whitelist-strict body shape: 8 fields. 5 required (arc_id,
// entity_id, role, status, idempotency_key); 3 optional/nullable
// (joined_at, left_at, notes). Unknown fields are silently
// ignored — same posture as every CP8+ create surface.
//
// One R2 audit artifact per mutation:
//   ${R2_PREFIX_V2_STAGING}mutations/arc_participations/${id}/${idempotency_key}.json
//   body = ArcParticipationAuditArtifact. DB-first / R2-second; on
//   R2 failure the receipt's `audit_mirror_deferred` flag flips and
//   `mutation_events.artifact_key` stays NULL as the drift detector
//   (same posture as every CP8+ admin write).
//
// Greenfield mechanics in B2 (none precedented across CP8+):
//   1. FK 23503 remap for invalid arc_id / entity_id (two new
//      branches in the loader's remap function).
//   2. Soft-merged-entity rejection at the loader: a
//      `SELECT id FROM entities WHERE id=$1 AND
//      merged_into_entity_id IS NULL FOR SHARE` inside the
//      transaction. Three outcomes:
//        - row found → proceed
//        - row missing → unknown_entity_id (typed)
//        - row exists soft-merged → entity_soft_merged (typed)
//      The DB FK does NOT enforce this rule; only the loader does,
//      mirroring the read-side soft-merge filter the public surface
//      already applies.
//
// 'winner' caveat (per the FISHSTANK_V2_PHASE_B2_..._FIRST_SLICE.md
//   §3.5 winner help-text rule):
//   - B2 PARSER ACCEPTS status='winner' as a valid enum value
//     (it's in PARTICIPATION_STATUSES_EDITABLE).
//   - B2 LOADER does NOT atomically update arcs.winner_entity_id.
//     A row created with status='winner' leaves the arc's denorm
//     column unchanged.
//   - Updating the arc's winner is deferred to B3's edit flow.
//
// The receipt envelope reuses `Envelope<TBody>` (provenance + body).

/**
 * Whitelist-strict request body for POST /v2/staging/arc-participations.
 *
 * Required:
 *   - arc_id, entity_id: stringified bigint (parser checks `^\d+$`)
 *   - role:   ∈ PARTICIPATION_ROLES_EDITABLE
 *   - status: ∈ PARTICIPATION_STATUSES_EDITABLE
 *   - idempotency_key: uuid v4
 *
 * Optional (default null when omitted):
 *   - joined_at, left_at: yyyy-mm-dd or null
 *   - notes: trimmed string ≤ ARC_PARTICIPATION_NOTES_MAX or null
 *
 * Cross-field parser pre-check: if both joined_at and left_at are
 * populated, `left_at` must be ≥ `joined_at` (else
 * `left_before_joined`). The DB has no CHECK for this; the parser
 * is the only enforcement layer.
 */
export interface ArcParticipationCreateRequest {
  arc_id: string
  entity_id: string
  role: ParticipationRoleEditable
  status: ParticipationStatusEditable
  joined_at?: string | null
  left_at?: string | null
  notes?: string | null
  /**
   * Per-arc category override (migration 0013). Optional. Omit (or
   * pass `null`) to mean "no override; use `entities.kind`". Same
   * enum domain as `EntityKindAdmin`. Validated against the closed
   * `EntityKindAdmin` enum at parse time; invalid values raise
   * `invalid_enum` with field='kind_override'.
   */
  kind_override?: EntityKindAdmin | null
  /**
   * Per-arc display alias FK (migration 0014). Optional. Omit or
   * pass `null` for "no override; render entity primary_name".
   * Pass a numeric stringified bigint to set the override to the
   * given `entity_aliases.id`. The alias MUST belong to the same
   * entity as `entity_id`; cross-entity selection is rejected by
   * the parser (using a SELECT pre-check) AND by the DB trigger as
   * defense-in-depth — the parser remap surfaces a typed
   * `display_alias_belongs_to_other_entity` error; the trigger
   * surface remaps the same way for direct DB writes.
   */
  display_alias_id?: string | null
  idempotency_key: string
}

/**
 * Notes max — `arc_participations.notes` is a TEXT column with no
 * DB-side bound. Cap of 1000 chars is curationally generous and
 * matches the audit body's payload budget.
 */
export const ARC_PARTICIPATION_NOTES_MAX = 1000 as const

/**
 * Full row snapshot for the receipt and audit blob. Same shape as
 * the B1 `ArcParticipationAdminItem` (the read shape) so the SPA
 * receipt and the per-arc list use identical typing.
 */
export type ArcParticipationRowSnapshot = ArcParticipationAdminItem

/**
 * Closed validation-error union for B2. Parser-side errors,
 * loader-side soft-merge pre-check, and DB-error remaps.
 *
 * The discriminated `code` lets the SPA render inline under the
 * offending field. Form-level errors (no specific field anchor)
 * include `invalid_body`, `idempotency_key_in_use`,
 * `unknown_arc_id` (FK violation — operator's arc picker stale).
 */
export type ArcParticipationValidationError =
  | { code: 'invalid_body' }
  | { code: 'arc_id_required'; field: 'arc_id' }
  | { code: 'invalid_arc_id'; field: 'arc_id' }
  | { code: 'entity_id_required'; field: 'entity_id' }
  | { code: 'invalid_entity_id'; field: 'entity_id' }
  | { code: 'role_required'; field: 'role' }
  | { code: 'status_required'; field: 'status' }
  | { code: 'invalid_enum'; field: string; allowed: readonly string[] }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'invalid_date'; field: 'joined_at' | 'left_at' }
  | { code: 'left_before_joined'; field: 'left_at' }
  | { code: 'notes_too_long'; field: 'notes'; max: number }
  | { code: 'unknown_arc_id'; field: 'arc_id' }
  | { code: 'unknown_entity_id'; field: 'entity_id' }
  | { code: 'entity_soft_merged'; field: 'entity_id' }
  | { code: 'participation_already_exists'; field: 'entity_id' }
  | { code: 'idempotency_key_in_use'; field: 'idempotency_key' }
  // Refinement Phase B3 — additive variants for the edit/delete
  // surface. `no_editable_fields` mirrors A3's empty-delta guard.
  // `participation_not_found` is the typed 404 for PATCH and
  // DELETE when the participation `:id` doesn't resolve.
  | { code: 'no_editable_fields' }
  | { code: 'participation_not_found' }
  // Migration 0014 — per-arc display alias FK. `invalid_alias_id`
  // covers non-numeric / negative / non-bigint-shaped strings.
  // `display_alias_not_found` covers a syntactically-valid id that
  // doesn't resolve in `entity_aliases`. `display_alias_belongs_to_other_entity`
  // covers the same-entity invariant (alias points at a different
  // entity's row) — surfaced both by the parser pre-check and by
  // the DB trigger remap.
  | { code: 'invalid_alias_id'; field: 'display_alias_id' }
  | { code: 'display_alias_not_found'; field: 'display_alias_id' }
  | { code: 'display_alias_belongs_to_other_entity'; field: 'display_alias_id' }

/**
 * Receipt body for B2 create + B3 edit. `prior_row` is always
 * null on create (mirror of A2's `ArcMutationReceiptBody`); on
 * B3 edit, both `row` (post-edit snapshot) and `prior_row`
 * (pre-edit snapshot) are populated.
 *
 * Refinement Phase B3 — `arc_side_effect` widens additively.
 * Populated on edits where the winner flow fired (status flipped
 * to/from 'winner' and `arcs.winner_entity_id` was touched).
 * Null/absent on every B2 create receipt and on every edit that
 * didn't trigger the flow.
 *
 * `audit_mirror_deferred` is true only when the post-commit R2
 * mirror failed.
 */
export interface ArcParticipationMutationReceiptBody {
  row: ArcParticipationRowSnapshot
  prior_row: ArcParticipationRowSnapshot | null
  mutation_event_id: string
  audit_mirror_deferred?: true
  /**
   * Refinement Phase B3 — populated only when the winner flow
   * fired and `arcs.winner_entity_id` was touched. See
   * `ArcParticipationArcSideEffect`.
   */
  arc_side_effect?: ArcParticipationArcSideEffect | null
}

export type ArcParticipationMutationReceipt =
  Envelope<ArcParticipationMutationReceiptBody>

/**
 * Refinement Phase B3 — side-effect record capturing the
 * arc-side change to `arcs.winner_entity_id` triggered by a
 * winner-flow edit (`arc_participations.status` flipped to/from
 * `'winner'`) or by deleting a winner participation. Surfaced on
 * the receipt envelope's body and inside the R2 audit artifact
 * so an auditor reading either has full visibility into the
 * multi-table change without a second query.
 *
 * The field is null/absent when:
 *   - the edit didn't touch `status`
 *   - the edit changed status between two non-winner values
 *   - the loader's defensive `AND winner_entity_id = entity_id`
 *     clause didn't match (i.e., the arc's winner was already
 *     pointing at a different participation, so the unset was a
 *     no-op)
 */
export interface ArcParticipationArcSideEffect {
  /** stringified bigint — the arc whose denorm column was touched. */
  arc_id: string
  /** stringified bigint or null — `arcs.winner_entity_id` BEFORE the write. */
  prior_winner_entity_id: string | null
  /** stringified bigint or null — `arcs.winner_entity_id` AFTER the write. */
  new_winner_entity_id: string | null
}

/**
 * Refinement Phase B3 — receipt body for a successful DELETE.
 * Distinct from `ArcParticipationMutationReceiptBody` (used for
 * create + edit) so the receipt's `deleted_row` field carries
 * the prior snapshot directly without ambiguity.
 *
 * `arc_side_effect` is populated only when the deleted row's
 * status was `'winner'` AND `arcs.winner_entity_id` was pointing
 * at this entity at delete time (defensive `AND` clause matched).
 */
export interface ArcParticipationDeleteReceiptBody {
  /** Full snapshot of the deleted row (incl entity ref). */
  deleted_row: ArcParticipationRowSnapshot
  /** stringified bigint — the `mutation_events` row id. */
  mutation_event_id: string
  audit_mirror_deferred?: true
  arc_side_effect?: ArcParticipationArcSideEffect | null
}

export type ArcParticipationDeleteReceipt =
  Envelope<ArcParticipationDeleteReceiptBody>

/**
 * R2 audit-artifact body. Mirrors `EntityAuditArtifactBody` /
 * `ArcAuditArtifactBody`.
 *
 * Refinement Phase B3 widens this additively:
 *   - `action`: `'create' | 'update' | 'delete'` (was `'create'` only)
 *   - `prior_row`: `ArcParticipationRowSnapshot | null` (was `null` only;
 *     populated on update + delete)
 *   - `new_row`: `ArcParticipationRowSnapshot | null` (was non-null;
 *     null only on delete)
 *   - `arc_side_effect?`: optional, populated when the winner
 *     flow fired
 *
 * B2-era audit blobs (action='create', prior_row=null,
 * new_row=<snapshot>, no arc_side_effect field) validate
 * unchanged under the wider type.
 */
export interface ArcParticipationAuditArtifactBody {
  target_table: 'arc_participations'
  target_id: string
  action: 'create' | 'update' | 'delete'
  prior_row: ArcParticipationRowSnapshot | null
  new_row: ArcParticipationRowSnapshot | null
  mutation_event_id: string
  actor_cookie_hash: string
  idempotency_key: string
  /**
   * Refinement Phase B3 — captured when the winner flow fired.
   * Null on every other write (and on every B2 audit blob).
   */
  arc_side_effect?: ArcParticipationArcSideEffect | null
}

export type ArcParticipationAuditArtifact =
  Envelope<ArcParticipationAuditArtifactBody>

// -- Arc participations admin edit + delete request shapes (B3) ---------

/**
 * Whitelist-strict PATCH body for the edit surface.
 *
 * Editable: role, status, joined_at (nullable), left_at (nullable),
 * notes (nullable). `idempotency_key` required.
 *
 * NOT editable: `arc_id`, `entity_id` — changing them is
 * conceptually a delete + create; the operator should DELETE the
 * row and POST a new one. Any `arc_id`/`entity_id` field in the
 * body is silently ignored by the parser (whitelist-strict).
 *
 * The empty-delta guard fires when the body contains only
 * `idempotency_key` (no editable fields supplied) — emits typed
 * `no_editable_fields`.
 *
 * Cross-field pre-check (loader-side, not parser-side because the
 * parser can't see the prior row): the merged shape (delta + prior
 * untouched fields) must have `left_at >= joined_at` when both
 * populated; else `left_before_joined`.
 *
 * If `status` flips to/from `'winner'`, the loader atomically
 * updates `arcs.winner_entity_id` per the audit doc §4 winner-flow
 * rules. The receipt envelope's `arc_side_effect` field captures
 * the arc-side change (or null if no flow fired).
 */
export interface ArcParticipationEditRequest {
  role?: ParticipationRoleEditable
  status?: ParticipationStatusEditable
  joined_at?: string | null
  left_at?: string | null
  notes?: string | null
  /**
   * Per-arc category override (migration 0013). Editable. Pass
   * `null` to clear the override (UI falls back to `entity.kind`),
   * or one of `EntityKindAdmin` to set it. Omit the field entirely
   * to leave the existing value untouched (whitelist-strict patch
   * posture — `undefined` means "don't touch", `null` means "clear").
   */
  kind_override?: EntityKindAdmin | null
  /**
   * Per-arc display alias FK (migration 0014). Editable. Pass `null`
   * to clear (card falls back to `entity.display_name`); pass a
   * stringified bigint to set. Omit to leave untouched (same
   * `undefined` vs. `null` posture as `kind_override`). Same-entity
   * invariant enforced by parser pre-check + DB trigger.
   */
  display_alias_id?: string | null
  idempotency_key: string
}

/**
 * Whitelist-strict DELETE body. Just the `idempotency_key`.
 * Cloudflare Workers' `request.json()` reads bodies on DELETE
 * requests; this matches B2's body-borne idempotency posture.
 *
 * If the deleted row's `status='winner'` AND
 * `arcs.winner_entity_id == entity_id`, the loader clears the
 * arc's denorm column inside the same transaction. The receipt
 * envelope's `arc_side_effect` captures the change.
 */
export interface ArcParticipationDeleteRequest {
  idempotency_key: string
}

// ─── Refinement Phase D — Entity Aliases admin surface ───────────────────
//
// Read + create + edit + delete on `entity_aliases`. The table
// already exists (migration 0002) with all uniqueness constraints
// (entity_aliases_unique_per_entity per (entity_id, normalized_alias);
// entity_aliases_one_primary_per_normal partial unique on
// normalized_alias WHERE is_primary=true). Phase D adds the admin
// CRUD layer on top of the existing public read posture.
//
//   GET    /v2/staging/entities/admin/:id/aliases.json
//     → EntityAliasesAdminListArtifact
//   POST   /v2/staging/entity-aliases
//     → EntityAliasMutationReceipt
//   PATCH  /v2/staging/entity-aliases/:id
//     → EntityAliasMutationReceipt
//   DELETE /v2/staging/entity-aliases/:id
//     → EntityAliasDeleteReceipt
//
// CSRF action label: 'entity-alias-write' (new label, distinct
// from CP17's 'entity-write' so the alias surface evolves
// independently). Used by all three mutation endpoints.
//
// Server-side normalization posture: clients never send
// `normalized_alias`. The worker computes it via
// `lib/alias-normalize.ts:normalizeAlias` and stores it. The admin
// read row exposes both `alias` (raw operator-entered or
// seed-ingested form) and `normalized_alias` (server-computed; the
// SPA renders it as a read-only field).
//
// Empty-normalized rejection: if normalizeAlias(alias) === '',
// the parser refuses with `empty_normalized_alias`. Examples:
// '!!!' (pure punctuation), '   ' (whitespace), pure CJK strings.
// The DB indexes can't distinguish empty-from-empty so we refuse
// to insert.
//
// Soft-merge posture: the loader's create flow checks
// `entities.merged_into_entity_id IS NULL` for the parent entity
// (FOR SHARE inside the transaction); soft-merged entities reject
// with `entity_soft_merged`. Edit + delete don't repeat the check
// — the SPA hides the section on merged entities; loader-level
// hygiene allows historical edits.

export const ENTITY_ALIAS_MAX_LENGTH = 200
export const ENTITY_ALIAS_SOURCE_MAX_LENGTH = 100

/**
 * Refinement Phase D — admin row for `entity_aliases`. Distinct
 * from CP1's `EntityAlias` (public wiki shape: only `alias` +
 * `is_primary`). The admin shape carries `normalized_alias` +
 * `source` + `created_at` + the row id so the operator can
 * reason about the full row.
 */
export interface EntityAliasAdminItem {
  /** `entity_aliases.id` — stringified bigint. */
  id: string
  /** stringified bigint */
  entity_id: string
  /** Raw operator-entered or seed-ingested form. */
  alias: string
  /** Server-computed via `normalizeAlias`. Read-only on the wire. */
  normalized_alias: string
  is_primary: boolean
  /** Free-form provenance ('admin', 'ingest', etc.). Nullable. */
  source: string | null
  /** ISO-8601 from `entity_aliases.created_at`. */
  created_at: string
}

/**
 * Body of `GET /v2/staging/entities/admin/:id/aliases.json`.
 *
 * `entity_id` echoes the requested id so the SPA can verify the
 * response matches the URL parameter without re-parsing the
 * route. Sort: `is_primary DESC, LOWER(alias) ASC, id ASC`.
 */
export interface EntityAliasesAdminListBody {
  entity_id: string
  aliases: readonly EntityAliasAdminItem[]
}

export type EntityAliasesAdminListArtifact =
  Envelope<EntityAliasesAdminListBody>

/**
 * Whitelist-strict POST body for the create surface.
 *
 * Required: `entity_id`, `alias`, `idempotency_key`.
 * Optional: `is_primary` (default false), `source` (default null;
 *   the worker may stamp 'admin' if null + idempotency-key flow
 *   demands a non-null source — implementation detail).
 *
 * Server normalizes; clients never send `normalized_alias`.
 */
export interface EntityAliasCreateRequest {
  entity_id: string
  alias: string
  is_primary?: boolean
  source?: string | null
  idempotency_key: string
}

/**
 * Whitelist-strict PATCH body for the edit surface.
 *
 * Editable: `alias`, `is_primary`, `source`. `entity_id` is NOT
 * editable in this slice — re-parenting an alias to a different
 * entity is conceptually delete+create; the operator should
 * DELETE the row and POST a new one.
 *
 * Empty-delta guard fires when only `idempotency_key` is in the
 * body — emits typed `no_editable_fields`.
 */
export interface EntityAliasEditRequest {
  alias?: string
  is_primary?: boolean
  source?: string | null
  idempotency_key: string
}

/**
 * Whitelist-strict DELETE body. Carries only `idempotency_key`.
 * Cloudflare Workers accept JSON bodies on DELETE; this matches
 * B3's body-borne idempotency posture.
 */
export interface EntityAliasDeleteRequest {
  idempotency_key: string
}

/**
 * Validation-error variants for the Phase D surface. All map to
 * HTTP 400 except `csrf_failed` (403, surfaced by the worker
 * shell) and `alias_not_found` / `unknown_entity_id` (404).
 *
 * `alias_already_exists` and `alias_already_primary_elsewhere`
 * are typed remaps of the two distinct 23505 paths from the DB:
 *   - entity_aliases_unique_per_entity → alias_already_exists
 *     (per-entity duplicate normalized_alias)
 *   - entity_aliases_one_primary_per_normal → alias_already_primary_elsewhere
 *     (global is_primary collision on the same normalized form)
 */
export type EntityAliasValidationError =
  | { code: 'invalid_body' }
  | { code: 'entity_id_required'; field: 'entity_id' }
  | { code: 'invalid_entity_id'; field: 'entity_id' }
  | { code: 'unknown_entity_id'; field: 'entity_id' }
  | { code: 'entity_soft_merged'; field: 'entity_id' }
  | { code: 'alias_required'; field: 'alias' }
  | { code: 'alias_too_long'; field: 'alias'; max: number }
  | { code: 'empty_normalized_alias'; field: 'alias' }
  | { code: 'alias_already_exists'; field: 'alias' }
  | { code: 'alias_already_primary_elsewhere'; field: 'alias' }
  | { code: 'source_too_long'; field: 'source'; max: number }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'idempotency_key_in_use'; field: 'idempotency_key' }
  | { code: 'no_editable_fields' }
  | { code: 'alias_not_found' }

/**
 * Receipt body for create + edit. `prior_row` is null on create;
 * on edit, both `row` (post-edit snapshot) and `prior_row`
 * (pre-edit snapshot) are populated.
 *
 * `audit_mirror_deferred` is true only when the post-commit R2
 * mirror failed.
 */
export interface EntityAliasMutationReceiptBody {
  row: EntityAliasAdminItem
  prior_row: EntityAliasAdminItem | null
  mutation_event_id: string
  audit_mirror_deferred?: true
}

export type EntityAliasMutationReceipt =
  Envelope<EntityAliasMutationReceiptBody>

/**
 * Receipt body for a successful DELETE. `deleted_row` carries the
 * pre-delete snapshot so the SPA can render an inline confirm.
 */
export interface EntityAliasDeleteReceiptBody {
  deleted_row: EntityAliasAdminItem
  mutation_event_id: string
  audit_mirror_deferred?: true
}

export type EntityAliasDeleteReceipt =
  Envelope<EntityAliasDeleteReceiptBody>

/**
 * R2 audit-artifact body. Mirrors the create+update+delete shape
 * established in B3 (action union, prior_row/new_row both
 * nullable to admit deletes).
 */
export interface EntityAliasAuditArtifactBody {
  target_table: 'entity_aliases'
  target_id: string
  action: 'create' | 'update' | 'delete'
  prior_row: EntityAliasAdminItem | null
  new_row: EntityAliasAdminItem | null
  mutation_event_id: string
  actor_cookie_hash: string
  idempotency_key: string
}

export type EntityAliasAuditArtifact =
  Envelope<EntityAliasAuditArtifactBody>

// ─── Entity social-links admin surface ───────────────────────────────────
//
// Mirrors the Refinement Phase D entity-aliases surface one-for-one
// against the existing `entity_social_links` table (migration 0002):
//
//   GET    /v2/staging/entities/admin/:id/social-links.json
//     → EntitySocialLinksAdminListArtifact
//   POST   /v2/staging/entity-social-links
//     → EntitySocialLinkMutationReceipt
//   PATCH  /v2/staging/entity-social-links/:id
//     → EntitySocialLinkMutationReceipt
//   DELETE /v2/staging/entity-social-links/:id
//     → EntitySocialLinkDeleteReceipt
//
// CSRF action label: 'entity-social-link-write' (new label,
// distinct from 'entity-write' / 'entity-alias-write' so the social-
// link surface evolves independently). Used by all three mutation
// endpoints.
//
// Schema reminder (migration 0002):
//   - platform: TEXT NOT NULL  (closed allowlist below)
//   - handle:   TEXT NOT NULL  (operator-entered, e.g. '@username')
//   - url:      TEXT (nullable)
//   - UNIQUE (entity_id, platform) — at most one link per platform per entity
//
// Soft-merge posture mirrors entity-aliases: create checks
// `entities.merged_into_entity_id IS NULL` (FOR SHARE); edit + delete
// don't repeat — the SPA hides the section on merged entities; loader-
// level hygiene allows historical edits.

/**
 * Closed platform allowlist for the admin add/edit surface. The DB
 * column is free-form TEXT so historical rows may carry other
 * values; the parser accepts only this set on writes.
 */
export const ENTITY_SOCIAL_LINK_PLATFORMS = [
  'x',
  'twitch',
  'youtube',
  'instagram',
  'tiktok',
  'kick',
  'website',
  'linktree',
  'soundcloud',
  'facebook',
] as const

export type EntitySocialLinkPlatform =
  (typeof ENTITY_SOCIAL_LINK_PLATFORMS)[number]

export const ENTITY_SOCIAL_LINK_HANDLE_MAX_LENGTH = 200
export const ENTITY_SOCIAL_LINK_URL_MAX_LENGTH = 2048

/**
 * Admin row for `entity_social_links`. Distinct from CP1's public
 * `EntitySocialLink` (which carries the same fields but no `id` /
 * `created_at`) — admin needs the row id for edit/delete and the
 * timestamp for context.
 */
export interface EntitySocialLinkAdminItem {
  /** `entity_social_links.id` — stringified bigint. */
  id: string
  /** stringified bigint */
  entity_id: string
  /**
   * Free-form on the DB side; on writes the parser narrows to the
   * `ENTITY_SOCIAL_LINK_PLATFORMS` allowlist. Read-side returns
   * whatever the DB has, so historical rows with non-allowlisted
   * platforms still surface.
   */
  platform: string
  /** Operator-entered handle (e.g. '@username'). Required on write. */
  handle: string
  /** Optional URL. Validated as http(s):// when present. */
  url: string | null
  /** ISO-8601 from `entity_social_links.created_at`. */
  created_at: string
}

/**
 * Body of `GET /v2/staging/entities/admin/:id/social-links.json`.
 *
 * `entity_id` echoes the requested id so the SPA can verify the
 * response matches the URL parameter. Sort: `LOWER(platform) ASC,
 * id ASC`.
 */
export interface EntitySocialLinksAdminListBody {
  entity_id: string
  social_links: readonly EntitySocialLinkAdminItem[]
}

export type EntitySocialLinksAdminListArtifact =
  Envelope<EntitySocialLinksAdminListBody>

/**
 * Whitelist-strict POST body for the create surface.
 *
 * Required: `entity_id`, `platform`, `handle`, `idempotency_key`.
 * Optional: `url` (nullable; validated as http(s):// when present).
 *
 * `(entity_id, platform)` is UNIQUE — adding a second row for the
 * same platform on the same entity rejects with `platform_already_exists`.
 */
export interface EntitySocialLinkCreateRequest {
  entity_id: string
  platform: EntitySocialLinkPlatform
  handle: string
  url?: string | null
  idempotency_key: string
}

/**
 * Whitelist-strict PATCH body for the edit surface.
 *
 * Editable: `platform`, `handle`, `url`. `entity_id` is NOT editable
 * — re-parenting a social link to a different entity is conceptually
 * delete+create.
 *
 * Empty-delta guard fires when only `idempotency_key` is in the body
 * — emits typed `no_editable_fields`.
 */
export interface EntitySocialLinkEditRequest {
  platform?: EntitySocialLinkPlatform
  handle?: string
  url?: string | null
  idempotency_key: string
}

/**
 * Whitelist-strict DELETE body. Carries only `idempotency_key`.
 */
export interface EntitySocialLinkDeleteRequest {
  idempotency_key: string
}

/**
 * Validation-error variants. Maps to HTTP 400 except `csrf_failed`
 * (403) and `social_link_not_found` / `unknown_entity_id` (404).
 *
 * `platform_already_exists` is the typed remap of the
 * `entity_social_links_entity_platform` UNIQUE constraint (23505).
 */
export type EntitySocialLinkValidationError =
  | { code: 'invalid_body' }
  | { code: 'entity_id_required'; field: 'entity_id' }
  | { code: 'invalid_entity_id'; field: 'entity_id' }
  | { code: 'unknown_entity_id'; field: 'entity_id' }
  | { code: 'entity_soft_merged'; field: 'entity_id' }
  | { code: 'platform_required'; field: 'platform' }
  | { code: 'invalid_platform'; field: 'platform'; allowed: readonly string[] }
  | { code: 'platform_already_exists'; field: 'platform' }
  | { code: 'handle_required'; field: 'handle' }
  | { code: 'handle_too_long'; field: 'handle'; max: number }
  | { code: 'invalid_url'; field: 'url' }
  | { code: 'url_too_long'; field: 'url'; max: number }
  | { code: 'invalid_uuid'; field: 'idempotency_key' }
  | { code: 'idempotency_key_in_use'; field: 'idempotency_key' }
  | { code: 'no_editable_fields' }
  | { code: 'social_link_not_found' }

/**
 * Receipt body for create + edit. `prior_row` is null on create;
 * on edit, both `row` and `prior_row` are populated.
 */
export interface EntitySocialLinkMutationReceiptBody {
  row: EntitySocialLinkAdminItem
  prior_row: EntitySocialLinkAdminItem | null
  mutation_event_id: string
  audit_mirror_deferred?: true
}

export type EntitySocialLinkMutationReceipt =
  Envelope<EntitySocialLinkMutationReceiptBody>

/**
 * Receipt body for a successful DELETE. `deleted_row` carries the
 * pre-delete snapshot.
 */
export interface EntitySocialLinkDeleteReceiptBody {
  deleted_row: EntitySocialLinkAdminItem
  mutation_event_id: string
  audit_mirror_deferred?: true
}

export type EntitySocialLinkDeleteReceipt =
  Envelope<EntitySocialLinkDeleteReceiptBody>

/**
 * R2 audit-artifact body. Mirrors the create+update+delete shape
 * established in B3 (action union, prior_row/new_row both nullable
 * to admit deletes).
 */
export interface EntitySocialLinkAuditArtifactBody {
  target_table: 'entity_social_links'
  target_id: string
  action: 'create' | 'update' | 'delete'
  prior_row: EntitySocialLinkAdminItem | null
  new_row: EntitySocialLinkAdminItem | null
  mutation_event_id: string
  actor_cookie_hash: string
  idempotency_key: string
}

export type EntitySocialLinkAuditArtifact =
  Envelope<EntitySocialLinkAuditArtifactBody>
